import { useState, useEffect, useCallback } from "react";
import {
  FlaskConical, Search, RefreshCw, Users, Eye, ChevronRight,
  Clock, Filter, X, Layers, Info,
  FileText, Phone, Mail, Calendar, User,
  Receipt, DollarSign, CalendarPlus, Settings, MessageSquare,
  Copy, Trash2, CheckCircle, AlertTriangle, Sparkles, ArrowRightLeft, Shield, Building2, Download,
  BarChart2, GitBranch
} from "lucide-react";
import { toast } from "sonner";
import SaniaConnect from "./components/SaniaConnect";

import UnifiedHeader from "./components/UnifiedHeader";
import MesRappelsWidget from './components/MesRappelsWidget';
import CommunicationInterneWidget from './components/SuperBatard/widgets/CommunicationInterneWidget';
import MaTodoListWidget from './components/SuperBatard/widgets/MaTodoListWidget';
import VisaIAWidget from './components/SuperBatard/widgets/VisaIAWidget';
import BanniereLeadsLive from './components/BanniereLeadsLive';
import BanniereNouveauR1 from './components/BanniereNouveauR1';
import getApiUrl from './utils/getApiUrl';
const API = `${getApiUrl()}/api`;

const authFetch = (url, options = {}) => {
  const token = localStorage.getItem("auth_token");
  return fetch(url, {
    ...options,
    credentials: "include",
    headers: { ...(options.headers || {}), ...(token ? { Authorization: `Bearer ${token}` } : {}) },
  });
};


// ═══════════════════════════════════════════════════════════════
// PANEL PRE-DEPLOY CHECKS — GO / NO-GO
// ═══════════════════════════════════════════════════════════════
function PreDeployChecksPanel() {
  const [running, setRunning] = useState(false);
  const [results, setResults] = useState(null);
  const [yaelProposals, setYaelProposals] = useState(null);

  const GLOBAL_COLLECTIONS = [
    "security_events","connection_logs","user_sessions","tenants","tenant_memberships",
    "users","scheduler_logs","automation_logs","admin_config","fs.files","fs.chunks",
    "tenant_counters","authorized_emails","rachel_v2_decisions","rachel_v2_business_config",
    "rachel_v2_prompts","rachel_v2_personas","rachel_v2_scenarios","rachel_v2_knowledge",
    "ringover_users"
  ];

  const runChecks = async () => {
    setRunning(true);
    setResults(null);
    const checks = {};

    // 1. Health check
    try {
      const r = await authFetch(`${API}/admin/diag/health-check`);
      const d = await r.json();
      const systems = d.systems || {};
      const failures = Object.entries(systems).filter(([,v]) => v?.status === "error");
      checks.health = { ok: r.ok && failures.length === 0, data: d, failures };
    } catch (e) { checks.health = { ok: false, error: e.message }; }

    // 2. Orphans scan
    try {
      const r = await authFetch(`${API}/admin/fix-tenant-orphans/scan`);
      const d = await r.json();
      const businessOrphans = (d.details || []).filter(
        c => c.orphans > 0 && !GLOBAL_COLLECTIONS.includes(c.collection)
      );
      const totalBizOrphans = businessOrphans.reduce((s, c) => s + c.orphans, 0);
      checks.orphans = { ok: true, total: d.total_orphans || 0, businessOrphans: totalBizOrphans, details: businessOrphans };
    } catch (e) { checks.orphans = { ok: false, error: e.message }; }

    // 3. Security events
    try {
      const r = await authFetch(`${API}/admin/security-events/summary?window=1h`);
      const d = await r.json();
      const blocked = (d.counts_by_event_type || {})["BLOCKED_WRITE_NO_TENANT"] || 0;
      const missing = (d.counts_by_event_type || {})["MISSING_TENANT_CONTEXT"] || 0;
      checks.security = { ok: blocked === 0, data: d, blocked, missing };
    } catch (e) { checks.security = { ok: false, error: e.message }; }

    // 4. Whoami
    try {
      const r = await authFetch(`${API}/admin/diag/whoami`);
      const d = await r.json();
      checks.whoami = {
        ok: !!d.resolved_tenant_id,
        tenant: d.resolved_tenant_id,
        rule: d.resolution_rule,
        explanation: d.resolution_explanation,
        memberships: d.memberships_count
      };
    } catch (e) { checks.whoami = { ok: false, error: e.message }; }

    // Verdict
    const noGo = !checks.health.ok || checks.security.blocked > 0;
    checks.verdict = noGo ? "NO-GO" : "GO";
    checks.verdictReason = noGo
      ? (!checks.health.ok ? "Health check en echec" : `${checks.security.blocked} ecritures bloquees sans tenant`)
      : "Tous les checks OK";
    checks.ts = new Date().toISOString();

    setResults(checks);
    setRunning(false);

    // ── Yael Auto-Propose: envoyer les resultats pour propositions automatiques ──
    try {
      const autoResp = await authFetch(`${API}/admin/actions/auto-propose`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          health_ok: checks.health.ok,
          health_failures: (checks.health.failures || []).map(([name]) => name),
          orphans_business: checks.orphans.businessOrphans || 0,
          orphans_details: (checks.orphans.details || []).map(c => ({ collection: c.collection, count: c.orphans })),
          security_blocked: checks.security.blocked || 0,
          security_events: checks.security.data?.counts_by_event_type || {},
        }),
      });
      if (autoResp.ok) {
        const autoData = await autoResp.json();
        setYaelProposals(autoData);
      }
    } catch (e) {
      console.error("Yael auto-propose error:", e);
    }
  };

  const Badge = ({ ok, label }) => (
    <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold ${ok ? "bg-emerald-500/20 text-emerald-400" : "bg-red-500/20 text-red-400"}`}>
      {ok ? <CheckCircle className="w-3.5 h-3.5" /> : <AlertTriangle className="w-3.5 h-3.5" />}
      {label}
    </span>
  );

  return (
    <div data-testid="predeploy-checks-panel">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h3 className="text-lg font-bold text-white flex items-center gap-2">
            <Shield className="w-5 h-5 text-cyan-400" />
            Pre-Deploy Checks
          </h3>
          <p className="text-sm text-slate-400 mt-1">Verification read-only avant deploiement</p>
        </div>
        <button
          data-testid="run-checks-btn"
          onClick={runChecks}
          disabled={running}
          className={`px-5 py-2.5 rounded-lg font-semibold text-sm flex items-center gap-2 transition-colors ${running ? "bg-slate-600 text-slate-400 cursor-wait" : "bg-cyan-600 hover:bg-cyan-500 text-white"}`}
        >
          {running ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Shield className="w-4 h-4" />}
          {running ? "Verification en cours..." : "Run Checks"}
        </button>
      </div>

      {results && (
        <div className="space-y-4">
          {/* Verdict Banner */}
          <div data-testid="verdict-banner" className={`rounded-xl p-5 border ${results.verdict === "GO" ? "bg-emerald-900/30 border-emerald-500/40" : "bg-red-900/30 border-red-500/40"}`}>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <span className={`text-3xl font-black tracking-tight ${results.verdict === "GO" ? "text-emerald-400" : "text-red-400"}`}>
                  {results.verdict}
                </span>
                <span className="text-sm text-slate-300">{results.verdictReason}</span>
              </div>
              <span className="text-xs text-slate-500">{new Date(results.ts).toLocaleString("fr-FR")}</span>
            </div>
          </div>

          {/* Yael Auto-Propose Banner */}
          {yaelProposals && yaelProposals.total_created > 0 && (
            <div data-testid="yael-auto-propose-banner" className="rounded-xl p-4 border border-violet-500/40 bg-violet-900/20">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <Shield className="w-5 h-5 text-violet-400" />
                  <div>
                    <span className="text-sm font-semibold text-violet-300">
                      Yael a propose {yaelProposals.total_created} action{yaelProposals.total_created > 1 ? "s" : ""} corrective{yaelProposals.total_created > 1 ? "s" : ""}
                    </span>
                    <div className="text-xs text-slate-400 mt-0.5">
                      {yaelProposals.proposals_created.map(p => p.label).join(", ")}
                      {yaelProposals.total_skipped > 0 && ` (${yaelProposals.total_skipped} ignoree${yaelProposals.total_skipped > 1 ? "s" : ""}: deja en attente)`}
                    </div>
                  </div>
                </div>
                <a
                  data-testid="yael-goto-pending-link"
                  href="/super-batard?section=yael-console"
                  onClick={(e) => {
                    e.preventDefault();
                    // Navigate to Super Batard > Yael Console
                    window.dispatchEvent(new CustomEvent("navigate-to-yael-console"));
                  }}
                  className="px-4 py-2 bg-violet-600 hover:bg-violet-500 rounded-lg text-xs font-semibold flex items-center gap-1.5 transition-colors text-white whitespace-nowrap"
                >
                  Voir dans Yael
                  <ChevronRight className="w-3.5 h-3.5" />
                </a>
              </div>
            </div>
          )}
          {yaelProposals && yaelProposals.total_created === 0 && yaelProposals.total_skipped > 0 && (
            <div className="rounded-xl p-3 border border-slate-700/40 bg-slate-800/30">
              <div className="flex items-center gap-2 text-xs text-slate-500">
                <CheckCircle className="w-3.5 h-3.5" />
                Yael : {yaelProposals.total_skipped} action{yaelProposals.total_skipped > 1 ? "s" : ""} deja en attente — pas de nouvelle proposition
              </div>
            </div>
          )}

          {/* Check Details */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Health */}
            <div data-testid="check-health" className="bg-slate-800/60 rounded-lg p-4 border border-slate-700/50">
              <div className="flex items-center justify-between mb-3">
                <span className="text-sm font-semibold text-white">Health Check</span>
                <Badge ok={results.health.ok} label={results.health.ok ? "OK" : "FAIL"} />
              </div>
              {results.health.failures?.length > 0 && (
                <div className="text-xs text-red-400 space-y-1">
                  {results.health.failures.map(([name]) => (
                    <div key={name}>Systeme en erreur : {name}</div>
                  ))}
                </div>
              )}
              {results.health.ok && <p className="text-xs text-slate-500">Tous les systemes repondent</p>}
            </div>

            {/* Security Events */}
            <div data-testid="check-security" className="bg-slate-800/60 rounded-lg p-4 border border-slate-700/50">
              <div className="flex items-center justify-between mb-3">
                <span className="text-sm font-semibold text-white">Security Events (1h)</span>
                <Badge ok={results.security.blocked === 0} label={results.security.blocked === 0 ? "OK" : `${results.security.blocked} BLOCKED`} />
              </div>
              <div className="text-xs space-y-1">
                <div className={results.security.blocked > 0 ? "text-red-400 font-semibold" : "text-slate-500"}>
                  BLOCKED_WRITE_NO_TENANT: {results.security.blocked}
                </div>
                <div className="text-slate-500">
                  MISSING_TENANT_CONTEXT: {results.security.missing}
                </div>
                {results.security.data?.counts_by_event_type && Object.entries(results.security.data.counts_by_event_type)
                  .filter(([k]) => k !== "MISSING_TENANT_CONTEXT" && k !== "BLOCKED_WRITE_NO_TENANT")
                  .map(([k, v]) => (
                    <div key={k} className="text-slate-500">{k}: {v}</div>
                  ))
                }
              </div>
            </div>

            {/* Orphans */}
            <div data-testid="check-orphans" className="bg-slate-800/60 rounded-lg p-4 border border-slate-700/50">
              <div className="flex items-center justify-between mb-3">
                <span className="text-sm font-semibold text-white">Orphelins Tenant</span>
                <Badge ok={results.orphans.ok} label={results.orphans.ok ? `${results.orphans.total} total` : "FAIL"} />
              </div>
              <div className="text-xs space-y-1 text-slate-500">
                <div>Collections metier avec orphelins: {results.orphans.details?.length || 0}</div>
                <div>Orphelins metier: {results.orphans.businessOrphans}</div>
                {results.orphans.details?.slice(0, 5).map(c => (
                  <div key={c.collection} className="text-amber-400/80 pl-2">
                    {c.collection}: {c.orphans}
                  </div>
                ))}
              </div>
            </div>

            {/* Whoami */}
            <div data-testid="check-whoami" className="bg-slate-800/60 rounded-lg p-4 border border-slate-700/50">
              <div className="flex items-center justify-between mb-3">
                <span className="text-sm font-semibold text-white">Whoami</span>
                <Badge ok={results.whoami.ok} label={results.whoami.ok ? "OK" : "FAIL"} />
              </div>
              <div className="text-xs space-y-1 text-slate-500">
                <div>Tenant: <span className="text-cyan-400">{results.whoami.tenant}</span></div>
                <div>Regle: <span className="text-white">{results.whoami.rule}</span></div>
                <div>{results.whoami.explanation}</div>
                <div>Memberships: {results.whoami.memberships}</div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// Mapping des icones pour les onglets
const TAB_ICONS = {
  FileText, Mail, Phone, Calendar, Receipt, DollarSign, CalendarPlus, Settings, Layers, Eye, User
};

// ═══════════════════════════════════════════════════════════════
// MODAL CLIENT - MODE PRÉVISUALISATION
// ═══════════════════════════════════════════════════════════════
const TestClientModal = ({ client, onClose }) => {
  const [activeTab, setActiveTab] = useState('infos');
  const [modalConfig, setModalConfig] = useState(null);
  const [dynamicTabs, setDynamicTabs] = useState([]);
  const [sheetData, setSheetData] = useState(null);
  const [loading, setLoading] = useState(true);

  // Configuration header (lecture seule - depuis la DB)
  const [headerConfig, setHeaderConfig] = useState({
    title_fields: ["nom", "prenom"],
    subtitle_fields: ["dpt", "logement", "row_number"],
    gradient_from: "purple-600",
    gradient_to: "pink-600"
  });

  // Charger la config complète
  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      try {
        // Charger la config complète du modal éditeur
        const [editorResp, tabsResp] = await Promise.all([
          authFetch(`${API}/config/modal-editor/main`),
          authFetch(`${API}/config/dynamic-tabs`)
        ]);
        
        const editorData = await editorResp.json();
        const tabsData = await tabsResp.json();
        
        setModalConfig(editorData);
        setDynamicTabs(tabsData.tabs || []);
        
        if (editorData.header) {
          setHeaderConfig(editorData.header);
        }

        // Charger les données du client
        if (client?.row_number) {
          const clientResp = await authFetch(`${API}/pipeline/client/by-row/${client.row_number}`);
          const clientData = await clientResp.json();
          setSheetData(clientData.client_details || clientData);
        }
      } catch (e) {
        console.error("Erreur chargement données:", e);
        toast.error("Erreur lors du chargement");
      }
      setLoading(false);
    };
    loadData();
  }, [client]);

  // Obtenir la valeur d'un champ
  const getFieldValue = (fieldId) => {
    if (!sheetData) return '';
    return sheetData[fieldId] || sheetData[fieldId.toLowerCase()] || '';
  };

  // Construire le titre du header
  const getHeaderTitle = () => {
    if (!sheetData) return client?.nom || 'Client';
    return headerConfig.title_fields
      .map(f => getFieldValue(f))
      .filter(Boolean)
      .join(' ') || 'Client';
  };

  // Construire le sous-titre du header
  const getHeaderSubtitle = () => {
    if (!sheetData) return '';
    const parts = headerConfig.subtitle_fields
      .map(f => {
        const val = getFieldValue(f);
        if (f === 'row_number') return `Ligne ${client?.row_number}`;
        if (f === 'dpt' && val) return `Dép. ${val}`;
        return val;
      })
      .filter(Boolean);
    return parts.join(' • ');
  };

  if (!client) return null;

  // Filtrer les onglets actifs
  const activeTabs = dynamicTabs.filter(t => t.enabled).sort((a, b) => a.order - b.order);

  return (
    <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
      <div className="bg-slate-900 rounded-2xl w-full max-w-5xl max-h-[90vh] overflow-hidden border border-slate-700 shadow-2xl flex flex-col">
        
        {/* HEADER - Mode lecture seule */}
        <div className={`relative p-4 bg-gradient-to-r from-${headerConfig.gradient_from} to-${headerConfig.gradient_to}`}>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-xl bg-white/20 flex items-center justify-center">
                <User className="w-6 h-6 text-white" />
              </div>
              <div>
                <h2 className="text-xl font-bold text-white">{getHeaderTitle()}</h2>
                <p className="text-white/70 text-sm">{getHeaderSubtitle()}</p>
              </div>
            </div>
            <button onClick={onClose} className="p-2 hover:bg-white/20 rounded-lg transition-colors">
              <X className="w-6 h-6 text-white" />
            </button>
          </div>
        </div>

        {/* SÉLECTIONS DYNAMIQUES (Onglets) */}
        <div className="border-b border-slate-700 px-4 flex items-center gap-2 overflow-x-auto bg-slate-800/50">
          {activeTabs.map(tab => {
            const IconComp = TAB_ICONS[tab.icon] || FileText;
            return (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center gap-2 px-4 py-3 border-b-2 transition-colors whitespace-nowrap ${
                  activeTab === tab.id 
                    ? 'border-purple-500 text-white bg-purple-500/10' 
                    : 'border-transparent text-slate-400 hover:text-white hover:bg-slate-700/30'
                }`}
              >
                <IconComp size={16} />
                {tab.label}
                {tab.badge_field && (
                  <span className="w-5 h-5 rounded-full bg-orange-500 text-white text-xs flex items-center justify-center">
                    {sheetData?.[tab.badge_field] || 0}
                  </span>
                )}
              </button>
            );
          })}
        </div>

        {/* CONTENU - Sections (lecture seule) */}
        <div className="flex-1 overflow-y-auto p-4">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <RefreshCw className="w-8 h-8 animate-spin text-purple-500" />
              <span className="ml-3 text-slate-400">Chargement...</span>
            </div>
          ) : activeTab === 'infos' ? (
            <div className="space-y-6">
              {/* Sections dynamiques */}
              {modalConfig?.sections?.filter(s => s.fields?.some(f => f.enabled)).map(section => (
                <div key={section.id} className="bg-slate-800/50 rounded-xl border border-slate-700/50 overflow-hidden">
                  <div className="bg-slate-700/30 px-4 py-3 border-b border-slate-700/50">
                    <h3 className="font-semibold text-white flex items-center gap-2">
                      <span className="text-lg">{section.icon}</span>
                      {section.label}
                      <span className="text-xs text-slate-500 ml-auto">
                        {section.fields?.filter(f => f.enabled).length} champs
                      </span>
                    </h3>
                  </div>
                  <div className="p-3 grid grid-cols-1 md:grid-cols-2 gap-2">
                    {section.fields?.filter(f => f.enabled).map(field => (
                      <div key={field.id} className="p-2 rounded-lg hover:bg-slate-700/30">
                        <span className="text-xs text-slate-500 block">{field.label}</span>
                        <span className={`text-sm ${getFieldValue(field.id) ? 'text-white' : 'text-slate-600 italic'}`}>
                          {getFieldValue(field.id) || 'Non renseigné'}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              ))}

              {(!modalConfig?.sections || modalConfig.sections.length === 0) && (
                <div className="text-center py-12 text-slate-400">
                  <Layers className="w-12 h-12 mx-auto mb-3 opacity-50" />
                  <p>Aucune section configurée</p>
                  <p className="text-sm">Configurez les sections dans Super Batard &gt; Champs Fiche Client</p>
                </div>
              )}
            </div>
          ) : (
            <div className="text-center py-12 text-slate-400">
              <Clock className="w-12 h-12 mx-auto mb-3 opacity-50" />
              <p>Contenu de l'onglet "{activeTabs.find(t => t.id === activeTab)?.label || activeTab}"</p>
              <p className="text-sm mt-2">À implémenter selon vos besoins</p>
            </div>
          )}
        </div>

        {/* FOOTER - Mode prévisualisation */}
        <div className="border-t border-slate-700 p-4 bg-slate-800/50 flex justify-between items-center">
          <div className="flex items-center gap-3">
            <span className="text-sm text-slate-400 flex items-center gap-2">
              <Eye size={16} className="text-purple-400" />
              Mode Prévisualisation
            </span>
          </div>
          
          <div className="text-sm text-slate-400">
            <span className="text-purple-400">Configuration</span> depuis Super Batard &gt; Champs Fiche Client
          </div>
        </div>
      </div>
    </div>
  );
};

// ═══════════════════════════════════════════════════════════════
// TESTS DASHBOARD MAIN

// ═══════════════════════════════════════════════════════════════
// DEDUP PANEL
// ═══════════════════════════════════════════════════════════════
const CONFIDENCE_CFG = {
  haute: { color: "text-red-400", bg: "bg-red-500/10", border: "border-red-500/30", label: "Haute" },
  moyenne: { color: "text-amber-400", bg: "bg-amber-500/10", border: "border-amber-500/30", label: "Moyenne" },
  basse: { color: "text-slate-400", bg: "bg-slate-500/10", border: "border-slate-500/30", label: "Basse" },
};

function DedupPanel() {
  const [groups, setGroups] = useState([]);
  const [scanning, setScanning] = useState(false);
  const [scanned, setScanned] = useState(false);
  const [totalClients, setTotalClients] = useState(0);
  const [iaResults, setIaResults] = useState({});
  const [iaLoading, setIaLoading] = useState({});
  const [resolving, setResolving] = useState({});

  const scan = async () => {
    console.log("[Dedup] Lancement du scan...");
    setScanning(true);
    try {
      const r = await authFetch(`${API}/admin/dedup/scan`);
      console.log("[Dedup] Response status:", r.status);
      if (r.ok) {
        const d = await r.json();
        console.log("[Dedup] Scan result:", d);
        setGroups(d.groups || []);
        setTotalClients(d.total_clients || 0);
        setScanned(true);
        if (d.duplicate_groups === 0) toast.success("Base propre ! Aucun doublon detecte.");
        else toast.info(`${d.duplicate_groups} groupe(s) de doublons detecte(s)`);
      } else {
        const errText = await r.text().catch(() => "");
        console.error("[Dedup] Scan erreur HTTP:", r.status, errText);
        toast.error(`Erreur scan (${r.status}): ${errText || "Verifiez la console"}`);
      }
    } catch (e) {
      console.error("[Dedup] Scan erreur reseau:", e);
      toast.error(`Erreur reseau: ${e.message}`);
    } finally {
      setScanning(false);
    }
  };

  const analyzeIA = async (group) => {
    setIaLoading((p) => ({ ...p, [group.id]: true }));
    try {
      const r = await authFetch(`${API}/admin/dedup/ia-analyze`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ keep: group.keep, remove: group.remove, match_type: group.match_type }),
      });
      if (r.ok) {
        const d = await r.json();
        setIaResults((p) => ({ ...p, [group.id]: d }));
      } else {
        toast.error(`Erreur IA (${r.status})`);
      }
    } catch (e) { toast.error(`Erreur IA: ${e.message}`); }
    setIaLoading((p) => ({ ...p, [group.id]: false }));
  };

  const resolve = async (keepId, removeId, groupId) => {
    setResolving((p) => ({ ...p, [groupId]: true }));
    try {
      const r = await authFetch(`${API}/admin/dedup/resolve`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ keep_id: keepId, remove_id: removeId }),
      });
      if (r.ok) {
        const d = await r.json();
        if (d.success) {
          setGroups((prev) => prev.filter((g) => g.id !== groupId));
          toast.success(`Doublon supprime, ${d.fields_merged} champs fusionnes`);
        } else {
          toast.error(d.error || "Erreur resolution");
        }
      } else {
        toast.error(`Erreur resolution (${r.status})`);
      }
    } catch (e) { toast.error(`Erreur resolution: ${e.message}`); }
    setResolving((p) => ({ ...p, [groupId]: false }));
  };

  const ClientCard = ({ client, label, labelColor }) => (
    <div className="bg-slate-900/50 rounded-lg p-3 flex-1">
      <div className="flex items-center justify-between mb-2">
        <span className={`text-xs font-bold uppercase ${labelColor}`}>{label}</span>
        <span className="text-xs text-slate-500">{client.filled_fields} champs</span>
      </div>
      <p className="font-semibold text-white text-sm">{client.nom} {client.prenom}</p>
      <p className="text-xs text-slate-400 font-mono mt-1">{client.client_id}</p>
      <div className="mt-2 space-y-1 text-xs">
        {client.telephone && <p className="flex items-center gap-1.5 text-slate-300"><Phone className="w-3 h-3" />{client.telephone}</p>}
        {client.email && <p className="flex items-center gap-1.5 text-slate-300"><Mail className="w-3 h-3" />{client.email}</p>}
        {client.new_statuts && <p className="flex items-center gap-1.5 text-slate-300"><Layers className="w-3 h-3" />{client.new_statuts}</p>}
        {client.source && <p className="flex items-center gap-1.5 text-slate-300"><Info className="w-3 h-3" />{client.source}</p>}
      </div>
    </div>
  );

  return (
    <div data-testid="dedup-panel" className="space-y-4">
      {/* Header + Scan button */}
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-bold flex items-center gap-2">
            <Copy className="w-5 h-5 text-amber-400" /> Nettoyage Doublons
          </h3>
          <p className="text-xs text-slate-400 mt-0.5">
            Detection par telephone, email et nom+prenom. Validation manuelle avec aide Christelle IA.
          </p>
        </div>
        <button
          data-testid="dedup-scan-btn"
          onClickCapture={(e) => { e.stopPropagation(); scan(); }}
          disabled={scanning}
          className="px-4 py-2 bg-amber-600 hover:bg-amber-500 disabled:opacity-50 rounded-lg text-sm font-semibold flex items-center gap-2"
          style={{ position: "relative", zIndex: 10 }}
        >
          {scanning ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />}
          {scanning ? "Scan en cours..." : "Lancer le scan"}
        </button>
      </div>

      {/* Results */}
      {scanned && (
        <div className="bg-slate-800/50 rounded-lg border border-slate-600/50 p-4 flex items-center gap-4">
          <div className="flex items-center gap-2 text-sm">
            <Users className="w-4 h-4 text-slate-400" />
            <span className="text-white font-semibold">{totalClients}</span>
            <span className="text-slate-400">clients analyses</span>
          </div>
          <div className="h-4 w-px bg-slate-600" />
          <div className="flex items-center gap-2 text-sm">
            {groups.length > 0 ? (
              <>
                <AlertTriangle className="w-4 h-4 text-amber-400" />
                <span className="text-amber-400 font-bold">{groups.length} doublon(s) detecte(s)</span>
              </>
            ) : (
              <>
                <CheckCircle className="w-4 h-4 text-emerald-400" />
                <span className="text-emerald-400 font-bold">Aucun doublon</span>
              </>
            )}
          </div>
        </div>
      )}

      {/* Groups */}
      {groups.map((g) => {
        const conf = CONFIDENCE_CFG[g.confidence] || CONFIDENCE_CFG.basse;
        const ia = iaResults[g.id];
        return (
          <div key={g.id} data-testid={`dedup-group-${g.id}`} className={`border ${conf.border} ${conf.bg} rounded-xl p-4 space-y-3`}>
            {/* Match info */}
            <div className="flex items-center justify-between flex-wrap gap-2">
              <div className="flex items-center gap-2">
                <span className={`text-xs font-bold px-2 py-0.5 rounded ${conf.bg} ${conf.color} border ${conf.border}`}>
                  {conf.label}
                </span>
                <span className="text-xs text-slate-400">
                  Match par <strong>{g.match_type}</strong>: <code className="text-white">{g.match_value}</code>
                </span>
              </div>
              {!ia && (
                <button
                  data-testid={`dedup-ia-${g.id}`}
                  onClick={() => analyzeIA(g)}
                  disabled={iaLoading[g.id]}
                  className="px-3 py-1.5 bg-violet-600 hover:bg-violet-500 disabled:opacity-50 rounded-lg text-xs font-semibold flex items-center gap-1.5"
                >
                  {iaLoading[g.id] ? <RefreshCw className="w-3 h-3 animate-spin" /> : <Sparkles className="w-3 h-3" />}
                  Christelle IA
                </button>
              )}
            </div>

            {/* IA recommendation */}
            {ia && (
              <div className="bg-violet-500/10 border border-violet-500/30 rounded-lg p-3">
                <p className="text-xs font-semibold text-violet-300 flex items-center gap-1.5 mb-1">
                  <Sparkles className="w-3 h-3" /> Recommandation Christelle IA
                  <span className={`ml-2 px-1.5 py-0.5 rounded text-xs ${CONFIDENCE_CFG[ia.confidence]?.bg || ""} ${CONFIDENCE_CFG[ia.confidence]?.color || "text-slate-400"}`}>
                    {ia.confidence}
                  </span>
                </p>
                <p className="text-sm text-slate-200">{ia.reason}</p>
                {ia.recommendation === "pas_doublon" && (
                  <p className="text-xs text-amber-400 mt-1 flex items-center gap-1"><Shield className="w-3 h-3" /> IA suggere que ce n'est pas un doublon</p>
                )}
              </div>
            )}

            {/* Side by side cards */}
            <div className="flex gap-3">
              <ClientCard client={g.keep} label="Fiche A" labelColor="text-emerald-400" />
              <div className="flex items-center">
                <ArrowRightLeft className="w-5 h-5 text-slate-600" />
              </div>
              <ClientCard client={g.remove} label="Fiche B" labelColor="text-red-400" />
            </div>

            {/* Actions */}
            <div className="flex items-center gap-2 pt-1">
              <button
                data-testid={`dedup-keep-a-${g.id}`}
                onClick={() => resolve(g.keep.client_id, g.remove.client_id, g.id)}
                disabled={resolving[g.id]}
                className="flex-1 py-2 bg-emerald-600 hover:bg-emerald-500 disabled:opacity-50 rounded-lg text-xs font-semibold flex items-center justify-center gap-1.5"
              >
                {resolving[g.id] ? <RefreshCw className="w-3 h-3 animate-spin" /> : <CheckCircle className="w-3 h-3" />}
                Garder A, supprimer B
              </button>
              <button
                data-testid={`dedup-keep-b-${g.id}`}
                onClick={() => resolve(g.remove.client_id, g.keep.client_id, g.id)}
                disabled={resolving[g.id]}
                className="flex-1 py-2 bg-blue-600 hover:bg-blue-500 disabled:opacity-50 rounded-lg text-xs font-semibold flex items-center justify-center gap-1.5"
              >
                <ArrowRightLeft className="w-3 h-3" />
                Garder B, supprimer A
              </button>
              <button
                onClick={() => setGroups((prev) => prev.filter((gr) => gr.id !== g.id))}
                className="py-2 px-3 bg-slate-700 hover:bg-slate-600 rounded-lg text-xs flex items-center gap-1.5 text-slate-300"
              >
                <X className="w-3 h-3" /> Ignorer
              </button>
            </div>
          </div>
        );
      })}

      {scanned && groups.length === 0 && (
        <div className="text-center py-10 bg-emerald-500/5 border border-emerald-500/20 rounded-xl">
          <CheckCircle className="w-12 h-12 mx-auto mb-3 text-emerald-400" />
          <p className="text-emerald-400 font-bold text-lg">Base propre !</p>
          <p className="text-sm text-slate-400 mt-1">Aucun doublon detecte parmi les {totalClients} clients</p>
        </div>
      )}
    </div>
  );
}



// ═══════════════════════════════════════════════════════════════
// FIX TENANT ORPHANS — Assigner tenant_default aux orphelins
// ═══════════════════════════════════════════════════════════════
function FixTenantOrphansPanel() {
  const [scanning, setScanning] = useState(false);
  const [fixing, setFixing] = useState(false);
  const [scanResults, setScanResults] = useState(null);
  const [fixResults, setFixResults] = useState(null);

  const runScan = async () => {
    setScanning(true);
    setScanResults(null);
    setFixResults(null);
    try {
      const res = await authFetch(`${API}/admin/fix-tenant-orphans/scan`);
      const data = await res.json();
      if (data.success) {
        setScanResults(data);
        if (data.total_orphans === 0) {
          toast.success("Aucun orphelin detecte. Tout est OK !");
        } else {
          toast.info(`${data.total_orphans} documents orphelins detectes dans ${Object.keys(data.collections_with_orphans).length} collections`);
        }
      } else {
        toast.error(data.detail || "Erreur scan");
      }
    } catch (e) {
      toast.error("Erreur: " + e.message);
    }
    setScanning(false);
  };

  const runFix = async () => {
    if (!window.confirm(`Confirmer : Assigner 'tenant_default' a ${scanResults?.total_orphans || 0} documents orphelins ? Cette operation est non-destructive (ajoute seulement tenant_id).`)) return;
    setFixing(true);
    try {
      const res = await authFetch(`${API}/admin/fix-tenant-orphans`, { method: "POST" });
      const data = await res.json();
      if (data.success) {
        setFixResults(data);
        toast.success(`${data.total_fixed} documents corriges !`);
        setScanResults(null);
      } else {
        toast.error(data.detail || "Erreur");
      }
    } catch (e) {
      toast.error("Erreur: " + e.message);
    }
    setFixing(false);
  };

  return (
    <div className="space-y-4">
      <div>
        <h2 className="text-lg font-semibold text-white flex items-center gap-2">
          <AlertTriangle className="w-5 h-5 text-amber-400" />
          Fix Tenant Orphelins (V2 — Toutes collections)
        </h2>
        <p className="text-sm text-slate-400 mt-1">
          Scanne TOUTES les collections de la base et assigne "tenant_default" aux documents sans tenant_id. Etape 1: Scanner, Etape 2: Corriger.
        </p>
      </div>

      <div className="flex gap-3">
        <button
          data-testid="btn-scan-orphans"
          onClick={runScan}
          disabled={scanning || fixing}
          className="px-5 py-2.5 bg-blue-600 hover:bg-blue-500 disabled:opacity-50 rounded-lg font-medium flex items-center gap-2"
        >
          {scanning ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />}
          {scanning ? "Scan en cours..." : "1. Scanner (Dry Run)"}
        </button>
        {scanResults && scanResults.total_orphans > 0 && (
          <button
            data-testid="btn-fix-orphans"
            onClick={runFix}
            disabled={fixing}
            className="px-5 py-2.5 bg-amber-600 hover:bg-amber-500 disabled:opacity-50 rounded-lg font-medium flex items-center gap-2"
          >
            {fixing ? <RefreshCw className="w-4 h-4 animate-spin" /> : <AlertTriangle className="w-4 h-4" />}
            {fixing ? "Correction en cours..." : `2. Corriger ${scanResults.total_orphans} orphelins`}
          </button>
        )}
      </div>

      {scanResults && (
        <div className="space-y-3">
          <div className={`${scanResults.total_orphans > 0 ? "bg-amber-500/10 border-amber-500/30" : "bg-green-500/10 border-green-500/30"} border rounded-lg p-4 text-center`}>
            <p className={`text-3xl font-bold ${scanResults.total_orphans > 0 ? "text-amber-400" : "text-green-400"}`}>{scanResults.total_orphans}</p>
            <p className={`text-sm ${scanResults.total_orphans > 0 ? "text-amber-300" : "text-green-300"}`}>
              documents orphelins dans {Object.keys(scanResults.collections_with_orphans).length} collections
              <span className="text-slate-500 ml-2">({scanResults.total_collections} collections scannees)</span>
            </p>
          </div>
          {Object.keys(scanResults.collections_with_orphans).length > 0 && (
            <div className="bg-slate-900/30 rounded-lg border border-slate-700/30 p-3 max-h-60 overflow-y-auto">
              <p className="text-xs text-slate-500 mb-2 font-medium">Collections avec orphelins :</p>
              {Object.entries(scanResults.collections_with_orphans).sort((a, b) => b[1].orphans - a[1].orphans).map(([col, v]) => (
                <p key={col} className="text-xs py-0.5 text-slate-400 flex justify-between">
                  <span className="font-medium text-white">{col}</span>
                  <span><span className="text-amber-400 font-medium">{v.orphans}</span> orphelins / {v.total} total</span>
                </p>
              ))}
            </div>
          )}
        </div>
      )}

      {fixResults && (
        <div className="space-y-3">
          <div className="bg-green-500/10 border border-green-500/30 rounded-lg p-4 text-center">
            <p className="text-3xl font-bold text-green-400">{fixResults.total_fixed}</p>
            <p className="text-sm text-green-300">documents corriges au total</p>
          </div>
          <div className="bg-slate-900/30 rounded-lg border border-slate-700/30 p-3 max-h-60 overflow-y-auto">
            {Object.entries(fixResults.results || {}).filter(([_, v]) => v.orphans > 0 || v.error).map(([col, v]) => (
              <p key={col} className={`text-xs py-0.5 ${v.error ? "text-red-400" : "text-slate-400"} flex justify-between`}>
                <span className="font-medium text-white">{col}</span>
                <span>{v.error ? `Erreur: ${v.error}` : `${v.fixed} corriges / ${v.orphans} orphelins`}</span>
              </p>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}


// ═══════════════════════════════════════════════════════════════
// CONSOLE DE DIAGNOSTIC — Debug production en temps reel
// ═══════════════════════════════════════════════════════════════
function DiagConsolePanel() {
  const [activeSection, setActiveSection] = useState("health");
  const [healthData, setHealthData] = useState(null);
  const [collections, setCollections] = useState(null);
  const [inspectData, setInspectData] = useState(null);
  const [selectedCol, setSelectedCol] = useState("");
  const [filterType, setFilterType] = useState("all");
  const [loading, setLoading] = useState({});
  const [searchCol, setSearchCol] = useState("");
  const [whoamiData, setWhoamiData] = useState(null);
  const [fullAudit, setFullAudit] = useState(null);

  const setLoadingKey = (key, val) => setLoading(prev => ({ ...prev, [key]: val }));

  const runHealthCheck = async () => {
    setLoadingKey("health", true);
    try {
      const res = await authFetch(`${API}/admin/diag/health-check`);
      const data = await res.json();
      setHealthData(data);
      // Also run whoami
      const res2 = await authFetch(`${API}/admin/diag/whoami`, { credentials: "include" });
      const data2 = await res2.json();
      setWhoamiData(data2);
      if (data.global_status === "ok") toast.success("Tous les systemes sont OK !");
      else if (data.vide_count > 0) toast.error(`${data.vide_count} systeme(s) vide(s) detecte(s)`);
    } catch (e) { toast.error("Erreur: " + e.message); }
    setLoadingKey("health", false);
  };

  const loadCollections = async () => {
    setLoadingKey("collections", true);
    try {
      const res = await authFetch(`${API}/admin/diag/collections`);
      const data = await res.json();
      setCollections(data.collections);
    } catch (e) { toast.error("Erreur: " + e.message); }
    setLoadingKey("collections", false);
  };

  const inspectCollection = async (colName, ft) => {
    setLoadingKey("inspect", true);
    setSelectedCol(colName);
    try {
      const res = await authFetch(`${API}/admin/diag/collection/${colName}?limit=10&filter_type=${ft || filterType}`);
      const data = await res.json();
      setInspectData(data);
    } catch (e) { toast.error("Erreur: " + e.message); }
    setLoadingKey("inspect", false);
  };

  const fixSingleCollection = async (colName) => {
    if (!window.confirm(`Corriger les orphelins de "${colName}" ?`)) return;
    setLoadingKey("fix-" + colName, true);
    try {
      const res = await authFetch(`${API}/admin/diag/fix-collection/${colName}`, { method: "POST" });
      const data = await res.json();
      if (data.success) {
        toast.success(`${data.fixed} documents corriges dans ${colName}`);
        loadCollections();
        if (selectedCol === colName) inspectCollection(colName, filterType);
      }
    } catch (e) { toast.error("Erreur: " + e.message); }
    setLoadingKey("fix-" + colName, false);
  };

  const runFullAudit = async () => {
    setLoadingKey("audit", true);
    try {
      const res = await authFetch(`${API}/admin/diag/full-audit`, { credentials: "include" });
      const data = await res.json();
      setFullAudit(data);
      toast.success("Audit complet termine en " + (data.summary?.audit_duration_seconds || "?") + "s");
    } catch (e) { toast.error("Erreur audit: " + e.message); }
    setLoadingKey("audit", false);
  };

  const copyAuditJSON = () => {
    if (!fullAudit) return;
    navigator.clipboard.writeText(JSON.stringify(fullAudit, null, 2));
    toast.success("Rapport JSON copie dans le presse-papier !");
  };

  const downloadAuditJSON = () => {
    if (!fullAudit) return;
    const blob = new Blob([JSON.stringify(fullAudit, null, 2)], { type: "application/json" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `audit-prod-${new Date().toISOString().slice(0,19).replace(/:/g,"-")}.json`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const statusIcon = (s) => s === "ok" ? "✅" : s === "vide" ? "❌" : "⚠️";
  const statusColor = (s) => s === "ok" ? "text-green-400" : s === "vide" ? "text-red-400" : "text-amber-400";

  const filteredCollections = collections?.filter(c =>
    !searchCol || c.name.toLowerCase().includes(searchCol.toLowerCase())
  ) || [];

  return (
    <div className="space-y-4">
      <div>
        <h2 className="text-lg font-semibold text-white flex items-center gap-2">
          <Shield className="w-5 h-5 text-cyan-400" />
          Console de Diagnostic
        </h2>
        <p className="text-sm text-slate-400 mt-1">
          Diagnostiquez et corrigez les problemes directement sur la version deployee.
        </p>
      </div>

      {/* Section tabs */}
      <div className="flex gap-2 flex-wrap">
        {[
          { id: "health", label: "Bilan de sante", icon: "💊" },
          { id: "collections", label: "Explorer les collections", icon: "🗄️" },
          { id: "inspect", label: "Inspecter une collection", icon: "🔍" },
          { id: "full-audit", label: "Audit Prod", icon: "📋" },
        ].map(s => (
          <button key={s.id} onClick={() => setActiveSection(s.id)}
            data-testid={`diag-tab-${s.id}`}
            className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${activeSection === s.id ? "bg-cyan-600 text-white" : "bg-slate-700 text-slate-400 hover:bg-slate-600"}`}>
            {s.icon} {s.label}
          </button>
        ))}
      </div>

      {/* HEALTH CHECK */}
      {activeSection === "health" && (
        <div className="space-y-3">
          <button data-testid="btn-health-check" onClick={runHealthCheck} disabled={loading.health}
            className="px-4 py-2 bg-cyan-600 hover:bg-cyan-500 disabled:opacity-50 rounded-lg text-sm font-medium flex items-center gap-2">
            {loading.health ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Shield className="w-4 h-4" />}
            {loading.health ? "Verification..." : "Lancer le bilan de sante"}
          </button>
          {healthData && (
            <div className="space-y-3">
              {/* Qui suis-je — Tenant resolution */}
              {whoamiData && (
                <div className={`border rounded-lg p-3 ${whoamiData.resolved_tenant_id === "tenant_default" ? "bg-green-500/10 border-green-500/30" : "bg-red-500/10 border-red-500/30"}`}>
                  <p className="text-sm font-medium text-white mb-1">Session utilisateur :</p>
                  <div className="text-xs text-slate-300 space-y-0.5">
                    <p>Email: <span className="text-cyan-400">{whoamiData.user_email || "N/A"}</span></p>
                    <p>Tenant resolu: <span className={`font-bold ${whoamiData.resolved_tenant_id === "tenant_default" ? "text-green-400" : "text-red-400"}`}>{whoamiData.resolved_tenant_id || "AUCUN"}</span>
                      {whoamiData.resolved_tenant_id && whoamiData.resolved_tenant_id !== "tenant_default" && (
                        <span className="text-red-300 ml-2">⚠️ Le fix a assigne "tenant_default" mais votre session utilise "{whoamiData.resolved_tenant_id}" — les donnees ne sont pas visibles !</span>
                      )}
                    </p>
                    {whoamiData.tenant_info && <p>Tenant: <span className="text-slate-200">{JSON.stringify(whoamiData.tenant_info)}</span></p>}
                    {whoamiData.data_check && (
                      <div className="mt-2 p-2 bg-slate-900/50 rounded">
                        <p className="text-xs font-medium text-amber-300 mb-1">Comparaison des donnees par tenant :</p>
                        {Object.entries(whoamiData.data_check).map(([k, v]) => (
                          <p key={k} className="text-[10px]">
                            <span className="text-slate-400">{k}:</span> <span className={v > 0 ? "text-green-400" : "text-red-400"}>{v}</span>
                          </p>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              )}
              <div className={`${healthData.global_status === "ok" ? "bg-green-500/10 border-green-500/30" : "bg-red-500/10 border-red-500/30"} border rounded-lg p-4`}>
                <div className="flex items-center justify-between">
                  <p className={`text-lg font-bold ${healthData.global_status === "ok" ? "text-green-400" : "text-red-400"}`}>
                    {healthData.global_status === "ok" ? "✅ Tous les systemes fonctionnent" : `⚠️ ${healthData.vide_count} probleme(s) detecte(s)`}
                  </p>
                  <div className="flex gap-3 text-sm">
                    <span className="text-green-400">{healthData.ok_count} OK</span>
                    {healthData.vide_count > 0 && <span className="text-red-400">{healthData.vide_count} Vide</span>}
                    {healthData.erreur_count > 0 && <span className="text-amber-400">{healthData.erreur_count} Erreur</span>}
                  </div>
                </div>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                {Object.entries(healthData.checks).map(([key, val]) => (
                  <div key={key} className={`p-3 rounded-lg border ${val.status === "ok" ? "bg-green-500/5 border-green-500/20" : "bg-red-500/5 border-red-500/20"}`}>
                    <div className="flex items-center justify-between">
                      <span className="text-sm font-medium text-white">{statusIcon(val.status)} {key}</span>
                      <span className={`text-xs font-medium ${statusColor(val.status)}`}>{val.status.toUpperCase()}</span>
                    </div>
                    <div className="text-xs text-slate-400 mt-1">
                      {Object.entries(val).filter(([k]) => k !== "status" && k !== "error").map(([k, v]) => (
                        <span key={k} className="mr-3">{k}: <span className="text-slate-300">{typeof v === "object" ? JSON.stringify(v) : String(v)}</span></span>
                      ))}
                      {val.error && <span className="text-red-400">{val.error}</span>}
                    </div>
                    {val.status === "vide" && (
                      <p className="text-xs text-red-300 mt-1 font-medium">
                        → Allez dans "Explorer les collections" pour verifier si les documents sont orphelins
                      </p>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* COLLECTIONS EXPLORER */}
      {activeSection === "collections" && (
        <div className="space-y-3">
          <div className="flex gap-2 items-center">
            <button data-testid="btn-load-collections" onClick={loadCollections} disabled={loading.collections}
              className="px-4 py-2 bg-cyan-600 hover:bg-cyan-500 disabled:opacity-50 rounded-lg text-sm font-medium flex items-center gap-2">
              {loading.collections ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Layers className="w-4 h-4" />}
              {loading.collections ? "Chargement..." : "Charger les collections"}
            </button>
            {collections && (
              <input data-testid="search-collection" type="text" placeholder="Filtrer les collections..."
                value={searchCol} onChange={e => setSearchCol(e.target.value)}
                className="px-3 py-2 bg-slate-900 border border-slate-700 rounded-lg text-sm text-white placeholder-slate-500 flex-1" />
            )}
          </div>
          {collections && (
            <div className="bg-slate-900/30 rounded-lg border border-slate-700/30 max-h-[400px] overflow-y-auto">
              <table className="w-full text-xs">
                <thead className="sticky top-0 bg-slate-800">
                  <tr className="text-slate-400">
                    <th className="px-3 py-2 text-left">Collection</th>
                    <th className="px-3 py-2 text-right">Total</th>
                    <th className="px-3 py-2 text-right">Avec tenant</th>
                    <th className="px-3 py-2 text-right">Orphelins</th>
                    <th className="px-3 py-2 text-center">Type</th>
                    <th className="px-3 py-2 text-center">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredCollections.filter(c => c.total > 0).sort((a, b) => b.orphans - a.orphans || b.total - a.total).map(c => (
                    <tr key={c.name} className={`border-t border-slate-700/30 hover:bg-slate-800/50 ${c.orphans > 0 ? "bg-red-500/5" : ""}`}>
                      <td className="px-3 py-1.5 text-white font-medium">{c.name}</td>
                      <td className="px-3 py-1.5 text-right text-slate-300">{c.total}</td>
                      <td className="px-3 py-1.5 text-right text-green-400">{c.with_tenant}</td>
                      <td className={`px-3 py-1.5 text-right font-medium ${c.orphans > 0 ? "text-red-400" : "text-slate-500"}`}>{c.orphans}</td>
                      <td className="px-3 py-1.5 text-center">
                        <span className={`px-1.5 py-0.5 rounded text-[10px] ${c.is_global ? "bg-blue-500/20 text-blue-300" : "bg-amber-500/20 text-amber-300"}`}>
                          {c.is_global ? "GLOBAL" : "TENANT"}
                        </span>
                      </td>
                      <td className="px-3 py-1.5 text-center space-x-1">
                        <button onClick={() => { setActiveSection("inspect"); inspectCollection(c.name, "all"); }}
                          className="px-2 py-0.5 bg-slate-700 hover:bg-slate-600 rounded text-[10px]">
                          Voir
                        </button>
                        {c.orphans > 0 && (
                          <button onClick={() => fixSingleCollection(c.name)}
                            disabled={loading["fix-" + c.name]}
                            className="px-2 py-0.5 bg-amber-600 hover:bg-amber-500 disabled:opacity-50 rounded text-[10px]">
                            {loading["fix-" + c.name] ? "..." : "Fix"}
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* INSPECT COLLECTION */}
      {activeSection === "inspect" && (
        <div className="space-y-3">
          <div className="flex gap-2 items-center flex-wrap">
            <input data-testid="inspect-col-input" type="text" placeholder="Nom de la collection..."
              value={selectedCol} onChange={e => setSelectedCol(e.target.value)}
              className="px-3 py-2 bg-slate-900 border border-slate-700 rounded-lg text-sm text-white placeholder-slate-500 w-64" />
            <select value={filterType} onChange={e => setFilterType(e.target.value)}
              data-testid="inspect-filter-select"
              className="px-3 py-2 bg-slate-900 border border-slate-700 rounded-lg text-sm text-white">
              <option value="all">Tous les documents</option>
              <option value="orphans">Orphelins seulement</option>
              <option value="with_tenant">Avec tenant_id</option>
            </select>
            <button onClick={() => inspectCollection(selectedCol, filterType)} disabled={loading.inspect || !selectedCol}
              data-testid="btn-inspect"
              className="px-4 py-2 bg-cyan-600 hover:bg-cyan-500 disabled:opacity-50 rounded-lg text-sm font-medium flex items-center gap-2">
              {loading.inspect ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />}
              Inspecter
            </button>
            {selectedCol && inspectData?.total > 0 && filterType === "orphans" && (
              <button onClick={() => fixSingleCollection(selectedCol)}
                data-testid="btn-fix-inspected"
                className="px-4 py-2 bg-amber-600 hover:bg-amber-500 rounded-lg text-sm font-medium">
                Corriger {inspectData.total} orphelins
              </button>
            )}
          </div>
          {inspectData && (
            <div className="space-y-2">
              <p className="text-sm text-slate-400">
                <span className="text-white font-medium">{inspectData.collection}</span> — {inspectData.total} documents ({inspectData.filter}) — Affichage: {inspectData.showing}
              </p>
              <div className="bg-slate-900/50 rounded-lg border border-slate-700/30 max-h-[400px] overflow-auto">
                {inspectData.documents.map((doc, i) => (
                  <details key={i} className="border-b border-slate-700/30">
                    <summary className="px-3 py-2 text-xs cursor-pointer hover:bg-slate-800/50 flex items-center gap-2">
                      <span className="text-slate-500">#{i + 1}</span>
                      <span className="text-white font-medium">{doc._id}</span>
                      {doc.tenant_id ? (
                        <span className="px-1.5 py-0.5 bg-green-500/20 text-green-300 rounded text-[10px]">tenant: {doc.tenant_id}</span>
                      ) : (
                        <span className="px-1.5 py-0.5 bg-red-500/20 text-red-300 rounded text-[10px]">PAS DE TENANT</span>
                      )}
                      {doc.email_address && <span className="text-cyan-400">{doc.email_address}</span>}
                      {doc.nom && <span className="text-slate-300">{doc.prenom} {doc.nom}</span>}
                      {doc.email && <span className="text-cyan-400">{doc.email}</span>}
                      {doc.type && <span className="text-amber-400">type: {doc.type}</span>}
                    </summary>
                    <pre className="px-3 py-2 text-[10px] text-slate-400 overflow-x-auto bg-slate-950/50 whitespace-pre-wrap break-all">
                      {JSON.stringify(doc, null, 2)}
                    </pre>
                  </details>
                ))}
                {inspectData.documents.length === 0 && (
                  <p className="px-3 py-4 text-sm text-slate-500 text-center">Aucun document trouve</p>
                )}
              </div>
            </div>
          )}
        </div>
      )}

      {/* FULL AUDIT PROD */}
      {activeSection === "full-audit" && (
        <div className="space-y-4">
          <div className="flex gap-2 items-center flex-wrap">
            <button data-testid="btn-run-full-audit" onClick={runFullAudit} disabled={loading.audit}
              className="px-4 py-2 bg-emerald-600 hover:bg-emerald-500 disabled:opacity-50 rounded-lg text-sm font-medium flex items-center gap-2">
              {loading.audit ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Shield className="w-4 h-4" />}
              {loading.audit ? "Audit en cours..." : "Lancer l'audit complet (read-only)"}
            </button>
            {fullAudit && (
              <>
                <button data-testid="btn-copy-audit" onClick={copyAuditJSON}
                  className="px-3 py-2 bg-slate-700 hover:bg-slate-600 rounded-lg text-sm flex items-center gap-1">
                  <Copy className="w-3.5 h-3.5" /> Copier JSON
                </button>
                <button data-testid="btn-download-audit" onClick={downloadAuditJSON}
                  className="px-3 py-2 bg-slate-700 hover:bg-slate-600 rounded-lg text-sm flex items-center gap-1">
                  <Download className="w-3.5 h-3.5" /> Telecharger JSON
                </button>
              </>
            )}
          </div>

          {fullAudit && (
            <div className="space-y-4">
              {/* Summary banner */}
              <div className="bg-gradient-to-r from-emerald-500/10 to-cyan-500/10 border border-emerald-500/30 rounded-xl p-4">
                <h3 className="text-base font-bold text-emerald-400 mb-2">Resume de l'audit</h3>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
                  <div><span className="text-slate-400">Docs business:</span> <span className="text-white font-bold">{fullAudit.summary?.total_business_docs?.toLocaleString()}</span></div>
                  <div><span className="text-slate-400">Orphelins:</span> <span className={`font-bold ${fullAudit.summary?.total_orphans_remaining > 0 ? "text-red-400" : "text-green-400"}`}>{fullAudit.summary?.total_orphans_remaining}</span></div>
                  <div><span className="text-slate-400">Fixes par script:</span> <span className="text-amber-400 font-bold">{fullAudit.summary?.total_fixed_by_script}</span></div>
                  <div><span className="text-slate-400">Multi-memberships:</span> <span className={`font-bold ${fullAudit.summary?.users_with_multi_membership > 0 ? "text-red-400" : "text-green-400"}`}>{fullAudit.summary?.users_with_multi_membership}</span></div>
                </div>
                <div className="mt-2 text-xs text-slate-500">
                  Genere le {fullAudit.generated_at} en {fullAudit.summary?.audit_duration_seconds}s — Tenants distincts: {fullAudit.summary?.distinct_tenant_ids?.join(", ")}
                </div>
              </div>

              {/* WHO AM I */}
              <div className="bg-slate-800/70 rounded-xl border border-blue-500/30 p-4">
                <h3 className="text-sm font-bold text-blue-400 mb-2">Who Am I</h3>
                <div className="grid grid-cols-2 gap-2 text-xs">
                  <div><span className="text-slate-500">Email:</span> <span className="text-white">{fullAudit.whoami?.user_email || "?"}</span></div>
                  <div><span className="text-slate-500">User ID:</span> <span className="text-white font-mono">{fullAudit.whoami?.user_id || "?"}</span></div>
                  <div><span className="text-slate-500">Source session:</span> <span className="text-white">{fullAudit.whoami?.session_source || "?"}</span></div>
                  <div><span className="text-slate-500">Tenant resolu:</span> <span className="text-amber-400 font-bold">{fullAudit.whoami?.resolved_tenant_id || "AUCUN"}</span></div>
                </div>
                {fullAudit.whoami?.all_memberships?.length > 0 && (
                  <div className="mt-2">
                    <p className="text-xs text-slate-500 mb-1">Memberships ({fullAudit.whoami.all_memberships.length}):</p>
                    {fullAudit.whoami.all_memberships.map((m, i) => (
                      <div key={i} className={`text-xs px-2 py-1 rounded mb-1 ${m.tenant_id === fullAudit.whoami.resolved_tenant_id ? "bg-amber-500/20 text-amber-300 border border-amber-500/30" : "bg-slate-700/50 text-slate-400"}`}>
                        {m.tenant_id === fullAudit.whoami.resolved_tenant_id && "→ "}{m.tenant_id} (role: {m.role}) {m.tenant_id === fullAudit.whoami.resolved_tenant_id && " ← ACTIF"}
                      </div>
                    ))}
                    {fullAudit.whoami.all_memberships.length > 1 && (
                      <p className="text-[10px] text-red-400 mt-1">
                        ⚠ {fullAudit.whoami.all_memberships.length} memberships actifs — la resolution est NON-DETERMINISTE (find_one sans sort)
                      </p>
                    )}
                  </div>
                )}
                {fullAudit.whoami?.user_record && (
                  <details className="mt-2">
                    <summary className="text-[10px] text-slate-500 cursor-pointer hover:text-slate-300">User record complet</summary>
                    <pre className="text-[10px] text-slate-400 mt-1 bg-slate-950/50 rounded p-2 overflow-auto max-h-32">{JSON.stringify(fullAudit.whoami.user_record, null, 2)}</pre>
                  </details>
                )}
              </div>

              {/* BUSINESS COLLECTIONS */}
              <div className="bg-slate-800/70 rounded-xl border border-slate-700/50 p-4">
                <h3 className="text-sm font-bold text-white mb-2">Collections Business</h3>
                <div className="overflow-x-auto">
                  <table className="w-full text-xs">
                    <thead>
                      <tr className="text-slate-500 border-b border-slate-700">
                        <th className="text-left py-1 px-2">Collection</th>
                        <th className="text-right py-1 px-1">Total</th>
                        <th className="text-right py-1 px-1">Orphelins</th>
                        <th className="text-right py-1 px-1">tenant_default</th>
                        <th className="text-right py-1 px-1">Autres</th>
                        <th className="text-right py-1 px-1">Fixes</th>
                        <th className="text-left py-1 px-2">Top tenants</th>
                        <th className="text-left py-1 px-2">Derniere MAJ</th>
                      </tr>
                    </thead>
                    <tbody>
                      {fullAudit.business_collections?.filter(c => c.exists && c.total > 0).map(c => (
                        <tr key={c.collection} className="border-b border-slate-800 hover:bg-slate-700/30">
                          <td className="py-1 px-2 text-white font-medium">{c.collection}</td>
                          <td className="text-right py-1 px-1">{c.total?.toLocaleString()}</td>
                          <td className={`text-right py-1 px-1 ${c.orphans_null_absent > 0 ? "text-red-400 font-bold" : "text-green-400"}`}>{c.orphans_null_absent}</td>
                          <td className="text-right py-1 px-1 text-cyan-300">{c.tenant_default?.toLocaleString()}</td>
                          <td className="text-right py-1 px-1 text-purple-300">{c.other_tenants}</td>
                          <td className={`text-right py-1 px-1 ${c.fixed_by_script > 0 ? "text-amber-400" : "text-slate-600"}`}>{c.fixed_by_script || "-"}</td>
                          <td className="py-1 px-2 text-slate-400 max-w-48 truncate">
                            {Object.entries(c.group_by_tenant || {}).map(([tid, cnt]) => `${tid}:${cnt}`).join(", ")}
                          </td>
                          <td className="py-1 px-2 text-slate-500 text-[10px]">
                            {c.max_dates?.updated_at?.slice(0,19) || c.max_dates?.created_at?.slice(0,19) || "-"}
                            {c.max_dates?.tenant_fixed_at && <span className="text-amber-500 ml-1">(fix: {c.max_dates.tenant_fixed_at.slice(0,19)})</span>}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>

              {/* SENSITIVE COLLECTIONS */}
              <div className="bg-slate-800/70 rounded-xl border border-red-500/20 p-4">
                <h3 className="text-sm font-bold text-red-400 mb-3">Collections Sensibles</h3>
                
                {/* Tenants */}
                <div className="mb-3">
                  <p className="text-xs font-semibold text-slate-300 mb-1">tenants ({fullAudit.sensitive_collections?.tenants?.total})</p>
                  {fullAudit.sensitive_collections?.tenants?.corrupted_by_fix_script > 0 && (
                    <div className="bg-red-500/10 border border-red-500/30 rounded p-2 mb-2 text-xs text-red-300">
                      ⚠ {fullAudit.sensitive_collections.tenants.corrupted_by_fix_script} tenant(s) corrompu(s) par le script Fix Orphelins (tenant_fixed_at present)
                      <ul className="mt-1 ml-3 list-disc">
                        {fullAudit.sensitive_collections.tenants.corrupted_list?.map((t, i) => (
                          <li key={i}>{t.name} (slug: {t.slug}) → tenant_id={t.tenant_id}, fixe le {t.fixed_at?.slice(0,19)}</li>
                        ))}
                      </ul>
                    </div>
                  )}
                  <details>
                    <summary className="text-[10px] text-slate-500 cursor-pointer hover:text-slate-300">Voir tous les tenants</summary>
                    <pre className="text-[10px] text-slate-400 mt-1 bg-slate-950/50 rounded p-2 overflow-auto max-h-48">{JSON.stringify(fullAudit.sensitive_collections?.tenants?.all_tenants, null, 2)}</pre>
                  </details>
                </div>

                {/* Users */}
                <div className="mb-3">
                  <p className="text-xs font-semibold text-slate-300 mb-1">users ({fullAudit.sensitive_collections?.users?.total})</p>
                  {fullAudit.sensitive_collections?.users?.anomalies_tenant_mismatch?.length > 0 && (
                    <div className="bg-amber-500/10 border border-amber-500/30 rounded p-2 mb-2 text-xs text-amber-300">
                      ⚠ {fullAudit.sensitive_collections.users.anomalies_tenant_mismatch.length} user(s) avec incoherence tenant_id:
                      <ul className="mt-1 ml-3 list-disc">
                        {fullAudit.sensitive_collections.users.anomalies_tenant_mismatch.map((a, i) => (
                          <li key={i}>{a.email}: users.tenant_id={a.tenant_id_in_users} vs 1er membership={a.first_membership_tenant}</li>
                        ))}
                      </ul>
                    </div>
                  )}
                  <div className="overflow-x-auto">
                    <table className="w-full text-[10px]">
                      <thead><tr className="text-slate-500"><th className="text-left px-1">Email</th><th className="text-left px-1">Role</th><th className="text-left px-1">tenant_id</th><th className="text-left px-1">default_tenant_id</th><th className="text-left px-1">Actif</th></tr></thead>
                      <tbody>
                        {fullAudit.sensitive_collections?.users?.all_users?.map((u, i) => (
                          <tr key={i} className="border-t border-slate-800">
                            <td className="px-1 py-0.5 text-white">{u.email}</td>
                            <td className="px-1 py-0.5">{u.role}</td>
                            <td className="px-1 py-0.5 text-cyan-300">{u.tenant_id}</td>
                            <td className="px-1 py-0.5 text-slate-400">{u.default_tenant_id || "-"}</td>
                            <td className="px-1 py-0.5">{u.is_active === false ? "❌" : "✅"}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>

                {/* Memberships */}
                <div className="mb-3">
                  <p className="text-xs font-semibold text-slate-300 mb-1">tenant_memberships ({fullAudit.sensitive_collections?.tenant_memberships?.total})</p>
                  {Object.keys(fullAudit.sensitive_collections?.tenant_memberships?.users_with_multiple_active_memberships || {}).length > 0 && (
                    <div className="bg-red-500/10 border border-red-500/30 rounded p-2 mb-2 text-xs text-red-300">
                      ⚠ Users avec memberships multiples (resolution non-deterministe) :
                      <ul className="mt-1 ml-3 list-disc">
                        {Object.entries(fullAudit.sensitive_collections.tenant_memberships.users_with_multiple_active_memberships).map(([uid, cnt]) => (
                          <li key={uid}>{uid}: {cnt} memberships actifs</li>
                        ))}
                      </ul>
                    </div>
                  )}
                  <details>
                    <summary className="text-[10px] text-slate-500 cursor-pointer hover:text-slate-300">Voir tous les memberships</summary>
                    <pre className="text-[10px] text-slate-400 mt-1 bg-slate-950/50 rounded p-2 overflow-auto max-h-48">{JSON.stringify(fullAudit.sensitive_collections?.tenant_memberships?.all_memberships, null, 2)}</pre>
                  </details>
                </div>

                {/* Sessions */}
                <div>
                  <p className="text-xs font-semibold text-slate-300 mb-1">user_sessions ({fullAudit.sensitive_collections?.user_sessions?.total})</p>
                  <div className="grid grid-cols-3 gap-2 text-xs mb-2">
                    <div><span className="text-slate-500">Orphelins:</span> <span className={fullAudit.sensitive_collections?.user_sessions?.orphans_without_tenant > 0 ? "text-red-400" : "text-green-400"}>{fullAudit.sensitive_collections?.user_sessions?.orphans_without_tenant}</span></div>
                    <div><span className="text-slate-500">Fixes par script:</span> <span className="text-amber-400">{fullAudit.sensitive_collections?.user_sessions?.fixed_by_script}</span></div>
                  </div>
                  <details>
                    <summary className="text-[10px] text-slate-500 cursor-pointer hover:text-slate-300">10 sessions recentes</summary>
                    <pre className="text-[10px] text-slate-400 mt-1 bg-slate-950/50 rounded p-2 overflow-auto max-h-48">{JSON.stringify(fullAudit.sensitive_collections?.user_sessions?.recent_10, null, 2)}</pre>
                  </details>
                </div>
              </div>

              {/* RAW JSON */}
              <details className="bg-slate-800/50 rounded-xl border border-slate-700/50 p-3">
                <summary className="text-xs text-slate-400 cursor-pointer hover:text-white font-medium">Rapport JSON brut complet</summary>
                <pre className="text-[10px] text-slate-400 mt-2 bg-slate-950/50 rounded p-3 overflow-auto max-h-[500px] whitespace-pre-wrap break-all">
                  {JSON.stringify(fullAudit, null, 2)}
                </pre>
              </details>
            </div>
          )}
        </div>
      )}
    </div>
  );
}


// ═══════════════════════════════════════════════════════════════
// SHEET RE-IMPORT PANEL — Re-import one-shot depuis Google Sheet
// ═══════════════════════════════════════════════════════════════
function SheetReimportPanel() {
  const [loading, setLoading] = useState(false);
  const [preview, setPreview] = useState(null);
  const [executing, setExecuting] = useState(false);
  const [results, setResults] = useState(null);
  const [selectedActions, setSelectedActions] = useState(new Set());
  const [filterType, setFilterType] = useState("all");

  const runPreview = async () => {
    setLoading(true);
    setPreview(null);
    setResults(null);
    try {
      const res = await authFetch(`${API}/admin/sheet-reimport/preview`, { method: "POST" });
      const data = await res.json();
      if (data.success) {
        setPreview(data);
        // Pre-select all actions
        const allIdx = new Set(data.actions.map((_, i) => i));
        setSelectedActions(allIdx);
        toast.success(`Preview: ${data.stats.dedupes} doublons, ${data.stats.updates} mises a jour, ${data.stats.inserts} nouveaux`);
      } else {
        toast.error(data.detail || "Erreur preview");
      }
    } catch (e) {
      toast.error("Erreur: " + e.message);
    }
    setLoading(false);
  };

  const executeImport = async () => {
    if (selectedActions.size === 0) return toast.error("Selectionnez au moins une action");
    if (!window.confirm(`Confirmer le re-import de ${selectedActions.size} action(s) ? Cette operation modifiera la base de donnees.`)) return;
    setExecuting(true);
    try {
      const actionsToSend = preview.actions.filter((_, i) => selectedActions.has(i));
      const res = await authFetch(`${API}/admin/sheet-reimport/execute`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ actions: actionsToSend }),
      });
      const data = await res.json();
      if (data.success) {
        setResults(data.results);
        toast.success(`Re-import termine: ${data.results.deduped} dedupes, ${data.results.updated} updates, ${data.results.inserted} inserts`);
      } else {
        toast.error(data.detail || "Erreur execution");
      }
    } catch (e) {
      toast.error("Erreur: " + e.message);
    }
    setExecuting(false);
  };

  const toggleAction = (idx) => {
    setSelectedActions(prev => {
      const next = new Set(prev);
      next.has(idx) ? next.delete(idx) : next.add(idx);
      return next;
    });
  };

  const selectAll = () => {
    if (!preview) return;
    const filtered = getFilteredActions();
    const allSelected = filtered.every((_, i) => selectedActions.has(preview.actions.indexOf(filtered[i])));
    const next = new Set(selectedActions);
    filtered.forEach(a => {
      const idx = preview.actions.indexOf(a);
      allSelected ? next.delete(idx) : next.add(idx);
    });
    setSelectedActions(next);
  };

  const getFilteredActions = () => {
    if (!preview) return [];
    if (filterType === "all") return preview.actions;
    return preview.actions.filter(a => a.type === filterType);
  };

  const filteredActions = getFilteredActions();

  const typeBadge = (type) => {
    const styles = {
      dedupe: "bg-red-500/20 text-red-400 border-red-500/30",
      update: "bg-blue-500/20 text-blue-400 border-blue-500/30",
      insert: "bg-green-500/20 text-green-400 border-green-500/30",
    };
    const labels = { dedupe: "DOUBLON", update: "MAJ", insert: "NOUVEAU" };
    return (
      <span className={`px-2 py-0.5 rounded text-xs font-bold border ${styles[type] || ""}`}>
        {labels[type] || type}
      </span>
    );
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-semibold text-white flex items-center gap-2">
            <RefreshCw className="w-5 h-5 text-green-400" />
            Re-import depuis Google Sheet
          </h2>
          <p className="text-sm text-slate-400 mt-1">
            Synchronise les donnees Sheet vers MongoDB. Resout les doublons par nom+prenom+dpt. Tel principal = TEL QHARE (col FO).
          </p>
        </div>
        <button
          data-testid="btn-sheet-preview"
          onClick={runPreview}
          disabled={loading}
          className="px-5 py-2.5 bg-green-600 hover:bg-green-500 disabled:opacity-50 rounded-lg font-medium flex items-center gap-2"
        >
          {loading ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />}
          {loading ? "Analyse en cours..." : "Lancer le preview"}
        </button>
      </div>

      {/* Stats */}
      {preview && (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
          <div className="bg-slate-900/50 rounded-lg p-3 border border-slate-700/50 text-center">
            <p className="text-2xl font-bold text-white">{preview.stats.sheet_total}</p>
            <p className="text-xs text-slate-400">Clients Sheet</p>
          </div>
          <div className="bg-slate-900/50 rounded-lg p-3 border border-slate-700/50 text-center">
            <p className="text-2xl font-bold text-white">{preview.stats.mongo_total}</p>
            <p className="text-xs text-slate-400">Clients MongoDB</p>
          </div>
          <div className="bg-red-500/10 rounded-lg p-3 border border-red-500/30 text-center">
            <p className="text-2xl font-bold text-red-400">{preview.stats.dedupes}</p>
            <p className="text-xs text-red-300">Doublons</p>
          </div>
          <div className="bg-blue-500/10 rounded-lg p-3 border border-blue-500/30 text-center">
            <p className="text-2xl font-bold text-blue-400">{preview.stats.updates}</p>
            <p className="text-xs text-blue-300">Mises a jour</p>
          </div>
          <div className="bg-green-500/10 rounded-lg p-3 border border-green-500/30 text-center">
            <p className="text-2xl font-bold text-green-400">{preview.stats.inserts}</p>
            <p className="text-xs text-green-300">Nouveaux</p>
          </div>
        </div>
      )}

      {/* Filters + Execute */}
      {preview && !results && (
        <div className="flex items-center justify-between">
          <div className="flex gap-2">
            {["all", "dedupe", "update", "insert"].map(f => (
              <button
                key={f}
                onClick={() => setFilterType(f)}
                className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${
                  filterType === f ? "bg-slate-600 text-white" : "bg-slate-800 text-slate-400 hover:bg-slate-700"
                }`}
              >
                {f === "all" ? `Tout (${preview.actions.length})` :
                 f === "dedupe" ? `Doublons (${preview.stats.dedupes})` :
                 f === "update" ? `MAJ (${preview.stats.updates})` :
                 `Nouveaux (${preview.stats.inserts})`}
              </button>
            ))}
          </div>
          <div className="flex gap-2 items-center">
            <button onClick={selectAll} className="px-3 py-1.5 bg-slate-700 hover:bg-slate-600 rounded-lg text-xs text-slate-300">
              Tout cocher/decocher
            </button>
            <span className="text-sm text-slate-400">{selectedActions.size} selectionne(s)</span>
            <button
              data-testid="btn-sheet-execute"
              onClick={executeImport}
              disabled={executing || selectedActions.size === 0}
              className="px-5 py-2 bg-red-600 hover:bg-red-500 disabled:opacity-50 rounded-lg font-medium flex items-center gap-2 text-sm"
            >
              {executing ? <RefreshCw className="w-4 h-4 animate-spin" /> : <CheckCircle className="w-4 h-4" />}
              {executing ? "Execution..." : "Executer"}
            </button>
          </div>
        </div>
      )}

      {/* Actions list */}
      {preview && !results && (
        <div className="space-y-2 max-h-[600px] overflow-y-auto pr-1">
          {filteredActions.map((action) => {
            const idx = preview.actions.indexOf(action);
            const selected = selectedActions.has(idx);
            return (
              <div
                key={idx}
                onClick={() => toggleAction(idx)}
                className={`p-3 rounded-lg border cursor-pointer transition-colors ${
                  selected
                    ? "bg-slate-800/80 border-green-500/40"
                    : "bg-slate-900/30 border-slate-700/30 opacity-60"
                }`}
              >
                <div className="flex items-center gap-3">
                  <input
                    type="checkbox"
                    checked={selected}
                    onChange={() => toggleAction(idx)}
                    className="w-4 h-4 rounded accent-green-500"
                  />
                  {typeBadge(action.type)}
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-white truncate">
                      {action.sheet_nom} <span className="text-slate-400">({action.sheet_dpt})</span>
                    </p>
                    <p className="text-xs text-slate-400">{action.detail}</p>
                  </div>
                  <div className="text-right text-xs space-y-0.5 shrink-0">
                    <p className="text-slate-400">Sheet row {action.sheet_row}</p>
                    {action.sheet_tel && <p className="text-green-400">Tel: {action.sheet_tel}</p>}
                    {action.mongo_tel && action.tel_changed && (
                      <p className="text-red-400 line-through">Ancien: {action.mongo_tel}</p>
                    )}
                    {action.mongo_count > 1 && (
                      <p className="text-amber-400">{action.mongo_count} fiches Mongo (rows: {action.mongo_rows?.join(", ")})</p>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Mongo-only clients */}
      {preview && preview.mongo_only?.length > 0 && !results && (
        <div className="mt-4">
          <h3 className="text-sm font-semibold text-slate-300 mb-2 flex items-center gap-2">
            <Info className="w-4 h-4 text-slate-400" />
            {preview.stats.mongo_only} client(s) uniquement dans MongoDB (pas sur le Sheet) — non touches
          </h3>
          <div className="bg-slate-900/30 rounded-lg border border-slate-700/30 p-3 max-h-48 overflow-y-auto">
            {preview.mongo_only.map((c, i) => (
              <p key={i} className="text-xs text-slate-500 py-0.5">
                {c.nom} ({c.dpt}) — row {c.row_number} — {c.etape || "?"} — {c.telephone || "pas de tel"}
              </p>
            ))}
          </div>
        </div>
      )}

      {/* Results */}
      {results && (
        <div className="space-y-3">
          <div className="grid grid-cols-4 gap-3">
            <div className="bg-green-500/10 rounded-lg p-3 border border-green-500/30 text-center">
              <p className="text-2xl font-bold text-green-400">{results.inserted}</p>
              <p className="text-xs text-green-300">Inseres</p>
            </div>
            <div className="bg-blue-500/10 rounded-lg p-3 border border-blue-500/30 text-center">
              <p className="text-2xl font-bold text-blue-400">{results.updated}</p>
              <p className="text-xs text-blue-300">Mis a jour</p>
            </div>
            <div className="bg-amber-500/10 rounded-lg p-3 border border-amber-500/30 text-center">
              <p className="text-2xl font-bold text-amber-400">{results.deduped}</p>
              <p className="text-xs text-amber-300">Dedupliques</p>
            </div>
            <div className="bg-red-500/10 rounded-lg p-3 border border-red-500/30 text-center">
              <p className="text-2xl font-bold text-red-400">{results.errors}</p>
              <p className="text-xs text-red-300">Erreurs</p>
            </div>
          </div>
          <div className="bg-slate-900/30 rounded-lg border border-slate-700/30 p-3 max-h-80 overflow-y-auto">
            {results.details?.map((d, i) => (
              <p key={i} className={`text-xs py-0.5 ${d.startsWith("ERREUR") ? "text-red-400" : d.startsWith("DEDUPE") ? "text-amber-400" : "text-slate-400"}`}>
                {d}
              </p>
            ))}
          </div>
          <button onClick={() => { setResults(null); setPreview(null); }} className="px-4 py-2 bg-slate-700 hover:bg-slate-600 rounded-lg text-sm">
            Relancer un preview
          </button>
        </div>
      )}
    </div>
  );
}


// ═══════════════════════════════════════════════════════════════
// AUDIT TENANT PANEL — Orphelins sans tenant
// ═══════════════════════════════════════════════════════════════
function AuditTenantPanel() {
  const [scanning, setScanning] = useState(false);
  const [scanned, setScanned] = useState(false);
  const [orphans, setOrphans] = useState([]);
  const [totalOrphans, setTotalOrphans] = useState(0);
  const [totalTest, setTotalTest] = useState(0);
  const [selected, setSelected] = useState(new Set());
  const [tenants, setTenants] = useState([]);
  const [selectedTenant, setSelectedTenant] = useState("");
  const [assigning, setAssigning] = useState(false);
  const [deleting, setDeleting] = useState(false);

  // Charger la liste des tenants
  useEffect(() => {
    const loadTenants = async () => {
      try {
        const r = await authFetch(`${API}/admin/tenants`);
        if (r.ok) {
          const data = await r.json();
          const list = data.tenants || data || [];
          setTenants(list);
          if (list.length === 1) setSelectedTenant(list[0].tenant_id || list[0].id);
        }
      } catch (e) { console.error("[AuditTenant] Erreur chargement tenants:", e); }
    };
    loadTenants();
  }, []);

  const scan = async () => {
    setScanning(true);
    try {
      const r = await authFetch(`${API}/admin/audit-tenant/scan`);
      if (r.ok) {
        const d = await r.json();
        const all = [...(d.leads_orphans || []), ...(d.pipeline_orphans || [])];
        setOrphans(all);
        setTotalOrphans(d.total_orphans || 0);
        setTotalTest(d.total_test || 0);
        setScanned(true);
        setSelected(new Set());
        if (d.total_orphans === 0) toast.success("Aucun orphelin detecte !");
        else toast.info(`${d.total_orphans} orphelin(s) detecte(s)`);
      } else {
        toast.error(`Erreur scan (${r.status})`);
      }
    } catch (e) { toast.error(`Erreur reseau: ${e.message}`); } finally { setScanning(false); }
  };

  const toggleSelect = (idx) => {
    setSelected(prev => {
      const next = new Set(prev);
      next.has(idx) ? next.delete(idx) : next.add(idx);
      return next;
    });
  };

  const selectAll = () => {
    if (selected.size === orphans.filter(o => !o.is_test).length) {
      setSelected(new Set());
    } else {
      setSelected(new Set(orphans.map((o, i) => !o.is_test ? i : null).filter(i => i !== null)));
    }
  };

  const assignToTenant = async () => {
    if (!selectedTenant) return toast.error("Selectionnez un tenant");
    const items = [...selected].map(i => orphans[i]).filter(Boolean);
    if (!items.length) return toast.error("Selectionnez des orphelins");
    setAssigning(true);
    try {
      const r = await authFetch(`${API}/admin/audit-tenant/assign`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          tenant_id: selectedTenant,
          ids: items.map(o => ({ id: o.id, collection: o.collection, row_number: o.row_number })),
        }),
      });
      if (r.ok) {
        const d = await r.json();
        toast.success(`${d.total_updated} document(s) rattache(s) a ${d.tenant_nom}`);
        scan(); // re-scan
      } else {
        toast.error(`Erreur (${r.status})`);
      }
    } catch (e) { toast.error(`Erreur: ${e.message}`); }
    setAssigning(false);
  };

  const deleteTests = async () => {
    if (!window.confirm("Supprimer tous les orphelins de test (pytest, TEST_*) ?")) return;
    setDeleting(true);
    try {
      const r = await authFetch(`${API}/admin/audit-tenant/delete-tests`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
      });
      if (r.ok) {
        const d = await r.json();
        toast.success(`${d.total_deleted} orphelin(s) de test supprimes`);
        scan(); // re-scan
      } else {
        toast.error(`Erreur (${r.status})`);
      }
    } catch (e) { toast.error(`Erreur: ${e.message}`); }
    setDeleting(false);
  };

  const realOrphans = orphans.filter(o => !o.is_test);
  const testOrphans = orphans.filter(o => o.is_test);

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-bold text-white flex items-center gap-2">
            <Building2 className="w-5 h-5 text-cyan-400" /> Audit Tenant
          </h3>
          <p className="text-sm text-slate-400 mt-1">Detecter et rattacher les documents orphelins (sans tenant)</p>
        </div>
        <button
          data-testid="audit-tenant-scan-btn"
          onClickCapture={(e) => { e.stopPropagation(); scan(); }}
          disabled={scanning}
          className="px-4 py-2 bg-cyan-600 hover:bg-cyan-500 disabled:opacity-50 rounded-lg text-sm font-semibold flex items-center gap-2"
          style={{ position: "relative", zIndex: 10 }}
        >
          {scanning ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />}
          {scanning ? "Scan en cours..." : "Lancer l'audit"}
        </button>
      </div>

      {/* Resultats */}
      {scanned && (
        <div className="bg-slate-800/50 rounded-lg border border-slate-600/50 p-4 flex items-center gap-4">
          <div className="flex items-center gap-2 text-sm">
            <Users className="w-4 h-4 text-slate-400" />
            <span className="text-white font-semibold">{totalOrphans}</span>
            <span className="text-slate-400">orphelin(s)</span>
          </div>
          <div className="h-4 w-px bg-slate-600" />
          <div className="flex items-center gap-2 text-sm">
            {totalOrphans === 0 ? (
              <><CheckCircle className="w-4 h-4 text-emerald-400" /><span className="text-emerald-400 font-bold">Tout est rattache</span></>
            ) : (
              <><AlertTriangle className="w-4 h-4 text-amber-400" /><span className="text-amber-400 font-bold">{realOrphans.length} reel(s), {testOrphans.length} test(s)</span></>
            )}
          </div>
        </div>
      )}

      {/* Zone de test orphelins */}
      {scanned && testOrphans.length > 0 && (
        <div className="bg-red-500/5 border border-red-500/20 rounded-lg p-4 flex items-center justify-between">
          <div>
            <p className="text-red-400 font-semibold text-sm">{testOrphans.length} orphelin(s) de test</p>
            <p className="text-xs text-slate-500 mt-0.5">Documents pytest / TEST_* sans tenant — supprimables</p>
          </div>
          <button
            data-testid="audit-delete-tests-btn"
            onClickCapture={(e) => { e.stopPropagation(); deleteTests(); }}
            disabled={deleting}
            className="px-3 py-1.5 bg-red-600/80 hover:bg-red-500 disabled:opacity-50 rounded text-xs font-semibold flex items-center gap-1.5"
          >
            {deleting ? <RefreshCw className="w-3 h-3 animate-spin" /> : <Trash2 className="w-3 h-3" />}
            Supprimer les tests
          </button>
        </div>
      )}

      {/* Toolbar rattachement */}
      {scanned && realOrphans.length > 0 && (
        <div className="bg-cyan-500/5 border border-cyan-500/20 rounded-lg p-4 space-y-3">
          <div className="flex items-center gap-3 flex-wrap">
            <label className="text-sm text-slate-300">Rattacher au tenant :</label>
            <select
              data-testid="audit-tenant-select"
              value={selectedTenant}
              onChange={(e) => setSelectedTenant(e.target.value)}
              className="bg-slate-800 border border-slate-600 rounded px-3 py-1.5 text-sm text-white"
            >
              <option value="">-- Choisir --</option>
              {tenants.map(t => (
                <option key={t.tenant_id || t.id} value={t.tenant_id || t.id}>{t.name || t.nom}</option>
              ))}
            </select>
            <button onClickCapture={(e) => { e.stopPropagation(); selectAll(); }} className="px-3 py-1.5 bg-slate-700 hover:bg-slate-600 rounded text-xs text-slate-300">
              {selected.size === realOrphans.length ? "Tout deselectionner" : "Tout selectionner"}
            </button>
            <button
              data-testid="audit-assign-btn"
              onClickCapture={(e) => { e.stopPropagation(); assignToTenant(); }}
              disabled={assigning || !selectedTenant || selected.size === 0}
              className="px-4 py-1.5 bg-cyan-600 hover:bg-cyan-500 disabled:opacity-50 rounded text-xs font-semibold flex items-center gap-1.5"
            >
              {assigning ? <RefreshCw className="w-3 h-3 animate-spin" /> : <Shield className="w-3 h-3" />}
              Rattacher ({selected.size})
            </button>
          </div>
        </div>
      )}

      {/* Table orphelins reels */}
      {scanned && realOrphans.length > 0 && (
        <div className="overflow-x-auto rounded-lg border border-slate-700/50">
          <table className="w-full">
            <thead className="bg-slate-900">
              <tr>
                <th className="px-3 py-2 w-10"></th>
                <th className="text-left px-3 py-2 text-xs uppercase text-slate-400">Nom</th>
                <th className="text-left px-3 py-2 text-xs uppercase text-slate-400">Prenom</th>
                <th className="text-left px-3 py-2 text-xs uppercase text-slate-400">Dpt</th>
                <th className="text-left px-3 py-2 text-xs uppercase text-slate-400">Source</th>
                <th className="text-left px-3 py-2 text-xs uppercase text-slate-400">Collection</th>
                <th className="text-left px-3 py-2 text-xs uppercase text-slate-400">Date</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-700/50">
              {realOrphans.map((o, idx) => {
                const realIdx = orphans.indexOf(o);
                return (
                  <tr key={`${o.id}-${idx}`} className="hover:bg-slate-700/30">
                    <td className="px-3 py-2 text-center">
                      <input
                        type="checkbox"
                        checked={selected.has(realIdx)}
                        onChange={() => toggleSelect(realIdx)}
                        className="rounded border-slate-500"
                      />
                    </td>
                    <td className="px-3 py-2 text-white text-sm font-medium">{o.nom || "-"}</td>
                    <td className="px-3 py-2 text-white text-sm">{o.prenom || "-"}</td>
                    <td className="px-3 py-2"><span className="bg-slate-700 px-2 py-0.5 rounded text-xs text-white">{o.departement || "-"}</span></td>
                    <td className="px-3 py-2 text-slate-300 text-sm">{o.source || "-"}</td>
                    <td className="px-3 py-2">
                      <span className={`text-xs px-2 py-0.5 rounded ${o.collection === "leads_entrants" ? "bg-blue-500/20 text-blue-400" : "bg-purple-500/20 text-purple-400"}`}>
                        {o.collection === "leads_entrants" ? "Lead" : "Pipeline"}
                      </span>
                    </td>
                    <td className="px-3 py-2 text-slate-400 text-xs">
                      {o.date_creation ? new Date(o.date_creation).toLocaleDateString('fr-FR') : "-"}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* Tout propre */}
      {scanned && totalOrphans === 0 && (
        <div className="text-center py-10 bg-emerald-500/5 border border-emerald-500/20 rounded-xl">
          <CheckCircle className="w-12 h-12 mx-auto mb-3 text-emerald-400" />
          <p className="text-emerald-400 font-bold text-lg">Tous les documents sont rattaches !</p>
          <p className="text-sm text-slate-400 mt-1">Aucun orphelin detecte</p>
        </div>
      )}
    </div>
  );
}



// ═══════════════════════════════════════════════════════════════
// PANEL NÉGO PIPELINE — COMPARAISON OPTION 1 vs OPTION 2
// ═══════════════════════════════════════════════════════════════
function NegoPipelineDemo() {
  const [opt1Avance, setOpt1Avance] = useState(null);
  const [opt1Solde, setOpt1Solde] = useState(null);
  const [opt1AvanceStep, setOpt1AvanceStep] = useState(null);
  const [opt2Step, setOpt2Step] = useState(null);
  const [opt2Solde, setOpt2Solde] = useState("");
  const [opt2SubStep, setOpt2SubStep] = useState(null);

  const AVANCE_STEPS_ANAH = [
    { id: "demande_amo", label: "Demande envoyée AMO", color: "#F59E0B" },
    { id: "avance_en_cours", label: "Avance en cours", color: "#8B5CF6" },
    { id: "avance_recue_sani", label: "Avance reçue Sani", color: "#10B981" },
  ];

  const SOLDE_OPTIONS = [
    { id: "client_100", label: "CLIENT 100%" },
    { id: "client_70", label: "CLIENT 70%" },
    { id: "client_35_anah_35", label: "CLIENT 35% - ANAH 35%" },
    { id: "anah_70", label: "ANAH 70% SEULE SOLUTION" },
    { id: "avance_50_50", label: "AVANCE + 50/50" },
    { id: "en_3_fois", label: "EN 3 FOIS" },
  ];

  const resetOpt1 = () => { setOpt1Avance(null); setOpt1Solde(null); setOpt1AvanceStep(null); };
  const resetOpt2 = () => { setOpt2Step(null); setOpt2Solde(""); setOpt2SubStep(null); };

  return (
    <div className="space-y-6">
      <div className="text-center mb-6">
        <h2 className="text-xl font-bold text-white">Parcours Justine — Négo Post-Octroi</h2>
        <p className="text-sm text-slate-400 mt-1">Client fictif : DUPONT Marie • Montant octroi : 15 200 €</p>
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">

        {/* OPTION 1 : Tout dans la pipeline */}
        <div className="bg-slate-800/70 border border-blue-500/30 rounded-xl p-5" data-testid="nego-option-1">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-bold text-blue-400">Option 1 — Tout en statuts pipeline</h3>
            <button onClick={resetOpt1} className="text-xs text-slate-500 hover:text-white px-2 py-1 rounded bg-slate-700/50">Reset</button>
          </div>
          <p className="text-xs text-slate-400 mb-4">L'avance ET le solde sont des branches dans la pipeline. Justine voit 2 flux parallèles.</p>

          <div className="space-y-3">
            <div className={`flex items-center gap-3 p-3 rounded-lg border transition-all ${!opt1Avance ? 'border-blue-500 bg-blue-500/10' : 'border-slate-700 bg-slate-900/50'}`}>
              <div className={`w-3 h-3 rounded-full ${!opt1Avance ? 'bg-blue-500 animate-pulse' : 'bg-blue-500'}`} />
              <span className="text-sm font-medium text-white">Sonder le client</span>
              {!opt1Avance && <span className="ml-auto text-[10px] bg-blue-500/20 text-blue-300 px-2 py-0.5 rounded-full">Étape actuelle</span>}
            </div>

            {!opt1Avance && (
              <div className="ml-6 space-y-3 border-l-2 border-dashed border-blue-500/30 pl-4">
                <p className="text-xs font-semibold text-slate-300">Justine choisit le type d'avance :</p>
                <div className="grid grid-cols-2 gap-2">
                  <button data-testid="opt1-client-30" onClick={() => { setOpt1Avance("client_30"); setOpt1AvanceStep("done"); }}
                    className="p-2.5 rounded-lg border border-emerald-500/40 bg-emerald-500/10 hover:bg-emerald-500/20 text-sm text-emerald-300 font-medium transition-all">
                    CLIENT 30%
                  </button>
                  <button data-testid="opt1-anah-30" onClick={() => { setOpt1Avance("anah_30"); setOpt1AvanceStep("demande_amo"); }}
                    className="p-2.5 rounded-lg border border-amber-500/40 bg-amber-500/10 hover:bg-amber-500/20 text-sm text-amber-300 font-medium transition-all">
                    ANAH 30%
                  </button>
                </div>
                <p className="text-xs font-semibold text-slate-300 mt-2">Et le type de solde (en parallèle) :</p>
                <div className="grid grid-cols-2 gap-1.5">
                  {SOLDE_OPTIONS.map(s => (
                    <button key={s.id} data-testid={`opt1-solde-${s.id}`} onClick={() => setOpt1Solde(s.id)}
                      className={`p-1.5 rounded-lg border text-[11px] font-medium transition-all ${opt1Solde === s.id ? 'border-purple-400 bg-purple-500/20 text-purple-300' : 'border-slate-600 bg-slate-800 text-slate-400 hover:border-slate-500'}`}>
                      {s.label}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {opt1Avance && (
              <>
                <div className="ml-6 border-l-2 border-emerald-500/30 pl-4 space-y-2">
                  <p className="text-[10px] uppercase tracking-wider text-emerald-400 font-bold">Flux Avance</p>
                  {opt1Avance === "client_30" && (
                    <div className="flex items-center gap-3 p-2.5 rounded-lg border border-emerald-500 bg-emerald-500/10">
                      <CheckCircle className="w-4 h-4 text-emerald-400" />
                      <span className="text-sm text-emerald-300 font-medium">CLIENT 30% — Fonds reçus</span>
                    </div>
                  )}
                  {opt1Avance === "anah_30" && AVANCE_STEPS_ANAH.map((step, i) => (
                    <div key={step.id} className={`flex items-center gap-3 p-2.5 rounded-lg border transition-all ${opt1AvanceStep === step.id ? 'border-amber-400 bg-amber-500/10' : 'border-slate-700/50 bg-slate-900/20'}`}>
                      <div className={`w-3 h-3 rounded-full ${opt1AvanceStep === step.id ? 'animate-pulse' : ''}`} style={{ backgroundColor: opt1AvanceStep === step.id ? step.color : '#475569' }} />
                      <span className={`text-sm font-medium ${opt1AvanceStep === step.id ? 'text-white' : 'text-slate-500'}`}>{step.label}</span>
                      {opt1AvanceStep === step.id && i < AVANCE_STEPS_ANAH.length - 1 && (
                        <button onClick={() => setOpt1AvanceStep(AVANCE_STEPS_ANAH[i + 1].id)}
                          className="ml-auto text-[10px] bg-amber-500/20 text-amber-300 px-2 py-0.5 rounded-full hover:bg-amber-500/30">Suivant</button>
                      )}
                      {opt1AvanceStep === step.id && i === AVANCE_STEPS_ANAH.length - 1 && (
                        <CheckCircle className="ml-auto w-4 h-4 text-emerald-400" />
                      )}
                    </div>
                  ))}
                </div>
                <div className="ml-6 border-l-2 border-purple-500/30 pl-4 space-y-2">
                  <p className="text-[10px] uppercase tracking-wider text-purple-400 font-bold">Flux Solde</p>
                  <div className="flex items-center gap-3 p-2.5 rounded-lg border border-purple-500/40 bg-purple-500/10">
                    <div className="w-3 h-3 rounded-full bg-purple-500" />
                    <span className="text-sm text-purple-300 font-medium">{SOLDE_OPTIONS.find(s => s.id === opt1Solde)?.label || "Non défini"}</span>
                  </div>
                </div>
                {(opt1Avance === "client_30" || opt1AvanceStep === "avance_recue_sani") && (
                  <div className="flex items-center gap-3 p-3 rounded-lg border border-emerald-500 bg-emerald-500/15 mt-2">
                    <CheckCircle className="w-5 h-5 text-emerald-400" />
                    <span className="text-sm font-bold text-emerald-300">ACCORD CHANTIER — À démarrer</span>
                  </div>
                )}
              </>
            )}
          </div>
        </div>

        {/* OPTION 2 : Pipeline avance + dropdown solde */}
        <div className="bg-slate-800/70 border border-emerald-500/30 rounded-xl p-5" data-testid="nego-option-2">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-bold text-emerald-400">Option 2 — Pipeline + champ fiche</h3>
            <button onClick={resetOpt2} className="text-xs text-slate-500 hover:text-white px-2 py-1 rounded bg-slate-700/50">Reset</button>
          </div>
          <p className="text-xs text-slate-400 mb-4">La pipeline suit le flux avance uniquement. Le solde est un <strong className="text-emerald-300">dropdown sur la fiche client</strong> rempli pendant la négo.</p>

          <div className="space-y-3">
            <div className={`flex items-center gap-3 p-3 rounded-lg border transition-all ${!opt2Step ? 'border-emerald-500 bg-emerald-500/10' : 'border-slate-700 bg-slate-900/50'}`}>
              <div className={`w-3 h-3 rounded-full ${!opt2Step ? 'bg-emerald-500 animate-pulse' : 'bg-emerald-500'}`} />
              <span className="text-sm font-medium text-white">Sonder le client</span>
              {!opt2Step && <span className="ml-auto text-[10px] bg-emerald-500/20 text-emerald-300 px-2 py-0.5 rounded-full">Étape actuelle</span>}
            </div>

            {!opt2Step && (
              <div className="ml-6 border-l-2 border-dashed border-emerald-500/30 pl-4 space-y-3">
                <p className="text-xs font-semibold text-slate-300">Justine choisit le type d'avance :</p>
                <div className="grid grid-cols-2 gap-2">
                  <button data-testid="opt2-client-30" onClick={() => { setOpt2Step("client_30"); setOpt2SubStep("done"); }}
                    className="p-2.5 rounded-lg border border-emerald-500/40 bg-emerald-500/10 hover:bg-emerald-500/20 text-sm text-emerald-300 font-medium transition-all">
                    CLIENT 30%
                  </button>
                  <button data-testid="opt2-anah-30" onClick={() => { setOpt2Step("anah_30"); setOpt2SubStep("demande_amo"); }}
                    className="p-2.5 rounded-lg border border-amber-500/40 bg-amber-500/10 hover:bg-amber-500/20 text-sm text-amber-300 font-medium transition-all">
                    ANAH 30%
                  </button>
                </div>
                <div className="mt-3 p-3 rounded-lg border border-slate-600 bg-slate-900/60">
                  <div className="flex items-center gap-2 mb-2">
                    <FileText className="w-3.5 h-3.5 text-slate-400" />
                    <span className="text-[10px] uppercase tracking-wider text-slate-400 font-bold">Fiche client — Champ solde</span>
                  </div>
                  <select data-testid="opt2-solde-dropdown" value={opt2Solde} onChange={e => setOpt2Solde(e.target.value)}
                    className="w-full bg-slate-800 border border-slate-600 rounded-lg px-3 py-2 text-sm text-white focus:border-emerald-500 focus:ring-1 focus:ring-emerald-500 outline-none">
                    <option value="">— Type de solde —</option>
                    {SOLDE_OPTIONS.map(s => (
                      <option key={s.id} value={s.id}>{s.label}</option>
                    ))}
                  </select>
                </div>
              </div>
            )}

            {opt2Step && (
              <>
                {opt2Step === "client_30" && (
                  <div className="ml-6 border-l-2 border-emerald-500/30 pl-4">
                    <div className="flex items-center gap-3 p-2.5 rounded-lg border border-emerald-500 bg-emerald-500/10">
                      <CheckCircle className="w-4 h-4 text-emerald-400" />
                      <span className="text-sm text-emerald-300 font-medium">CLIENT 30% — Fonds reçus</span>
                    </div>
                  </div>
                )}
                {opt2Step === "anah_30" && (
                  <div className="ml-6 border-l-2 border-amber-500/30 pl-4 space-y-2">
                    {AVANCE_STEPS_ANAH.map((step, i) => (
                      <div key={step.id} className={`flex items-center gap-3 p-2.5 rounded-lg border transition-all ${opt2SubStep === step.id ? 'border-amber-400 bg-amber-500/10' : 'border-slate-700/50 bg-slate-900/20'}`}>
                        <div className={`w-3 h-3 rounded-full ${opt2SubStep === step.id ? 'animate-pulse' : ''}`} style={{ backgroundColor: opt2SubStep === step.id ? step.color : '#475569' }} />
                        <span className={`text-sm font-medium ${opt2SubStep === step.id ? 'text-white' : 'text-slate-500'}`}>{step.label}</span>
                        {opt2SubStep === step.id && i < AVANCE_STEPS_ANAH.length - 1 && (
                          <button onClick={() => setOpt2SubStep(AVANCE_STEPS_ANAH[i + 1].id)}
                            className="ml-auto text-[10px] bg-amber-500/20 text-amber-300 px-2 py-0.5 rounded-full hover:bg-amber-500/30">Suivant</button>
                        )}
                        {opt2SubStep === step.id && i === AVANCE_STEPS_ANAH.length - 1 && (
                          <CheckCircle className="ml-auto w-4 h-4 text-emerald-400" />
                        )}
                      </div>
                    ))}
                  </div>
                )}
                <div className="ml-6 pl-4">
                  <div className="p-2.5 rounded-lg border border-slate-600 bg-slate-900/40">
                    <div className="flex items-center gap-2">
                      <FileText className="w-3.5 h-3.5 text-slate-400" />
                      <span className="text-xs text-slate-400">Solde sur fiche :</span>
                      <span className="text-xs font-medium text-purple-300">{SOLDE_OPTIONS.find(s => s.id === opt2Solde)?.label || "Non défini"}</span>
                    </div>
                  </div>
                </div>
                {(opt2Step === "client_30" || opt2SubStep === "avance_recue_sani") && (
                  <div className="flex items-center gap-3 p-3 rounded-lg border border-emerald-500 bg-emerald-500/15 mt-2">
                    <CheckCircle className="w-5 h-5 text-emerald-400" />
                    <span className="text-sm font-bold text-emerald-300">ACCORD CHANTIER — À démarrer</span>
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      </div>

      {/* Comparison summary */}
      <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
        <div className="bg-blue-500/5 border border-blue-500/20 rounded-xl p-4">
          <h4 className="text-sm font-bold text-blue-400 mb-2">Option 1 — Résumé</h4>
          <ul className="text-xs text-slate-400 space-y-1">
            <li>+ Tout visible dans la pipeline, 2 flux parallèles</li>
            <li>+ Chaque statut de solde est traçable dans le workflow</li>
            <li>- Plus de colonnes dans le kanban</li>
            <li>- Combinaisons avance x solde = beaucoup de branches</li>
          </ul>
        </div>
        <div className="bg-emerald-500/5 border border-emerald-500/20 rounded-xl p-4">
          <h4 className="text-sm font-bold text-emerald-400 mb-2">Option 2 — Résumé</h4>
          <ul className="text-xs text-slate-400 space-y-1">
            <li>+ Pipeline simple et lisible (que le flux avance)</li>
            <li>+ Solde comme info sur la fiche, pas de branches</li>
            <li>- Le solde n'apparaît pas dans le kanban pipeline</li>
            <li>- Moins de traçabilité workflow sur le solde</li>
          </ul>
        </div>
      </div>
    </div>
  );
}


// ═══════════════════════════════════════════════════════════════
// NÉGO JUSTINE TEST — Approche A (pills expansion) + Approche B (pills flat)
// ═══════════════════════════════════════════════════════════════
function NegoJustineTest() {
  const [selectedPill, setSelectedPill] = useState(null);
  const [expandedGroup, setExpandedGroup] = useState(null);

  const FAKE = {
    r2_a_planifier: 3, r2_nrp: 1, r2_a_rappeler: 2, r2_refus: 0,
    r2_planifie: 4, r2_realise: 1,
    sonder: 2, nego_client: 3, client_30: 1,
    anah_30_demande_amo: 1, anah_30_en_cours: 2, avance_recue_sani: 1,
    accord_chantier: 1,
    rac_cheque: 0, rac_echelonne: 1, rac_octroi: 0,
    paiement_partiel: 0, solde: 2, attente_octroi: 1
  };

  const GROUPS = {
    r2: {
      label: "PRISE R2", color: "#3B82F6",
      pills: [
        { id: "r2_a_planifier", label: "R2 à planifier", color: "#3B82F6" },
        { id: "r2_nrp", label: "R2 NRP", color: "#EF4444" },
        { id: "r2_a_rappeler", label: "R2 à rappeler", color: "#F59E0B" },
        { id: "r2_refus", label: "R2 Refus", color: "#6B7280" },
        { id: "r2_planifie", label: "R2 Planifié", color: "#8B5CF6" },
        { id: "r2_realise", label: "R2 Réalisé", color: "#10B981" },
      ]
    },
    nego: {
      label: "NÉGO AVANCE", color: "#F59E0B",
      pills: [
        { id: "sonder", label: "Sonder le client", color: "#3B82F6" },
        { id: "nego_client", label: "Négo client", color: "#8B5CF6" },
        { id: "client_30", label: "Client 30%", color: "#10B981" },
        { id: "anah_30_demande_amo", label: "ANAH 30% - Demande AMO", color: "#F59E0B" },
        { id: "anah_30_en_cours", label: "ANAH 30% - En cours", color: "#F97316" },
        { id: "avance_recue_sani", label: "Avance reçue Sani", color: "#22C55E" },
      ]
    },
    rac: {
      label: "RAC", color: "#A855F7",
      pills: [
        { id: "rac_cheque", label: "Chèque déposé", color: "#22C55E" },
        { id: "rac_echelonne", label: "Échelonné", color: "#F59E0B" },
        { id: "rac_octroi", label: "Octroi ANAH", color: "#8B5CF6" },
        { id: "paiement_partiel", label: "Paiement partiel", color: "#06B6D4" },
        { id: "solde", label: "Soldé", color: "#10B981" },
        { id: "attente_octroi", label: "En attente octroi", color: "#A855F7" },
      ]
    },
    chantier: {
      label: "CHANTIER", color: "#10B981",
      pills: [
        { id: "accord_chantier", label: "Accord chantier", color: "#10B981" },
      ]
    }
  };

  const groupTotal = (groupId) => GROUPS[groupId].pills.reduce((s, p) => s + (FAKE[p.id] || 0), 0);

  const PillBadge = ({ pill, isSelected, onClick, size = "normal" }) => {
    const count = FAKE[pill.id] || 0;
    return (
      <button onClick={onClick}
        className={`inline-flex items-center gap-1.5 rounded-full transition-all ${
          size === "small" ? "px-2.5 py-1 text-[10px]" : "px-3 py-1.5 text-xs"
        } font-medium ${
          isSelected
            ? "ring-2 ring-white/30 shadow-lg scale-105"
            : "hover:scale-105 hover:shadow-md"
        }`}
        style={{
          backgroundColor: isSelected ? pill.color : `${pill.color}20`,
          color: isSelected ? '#fff' : pill.color,
          borderWidth: 1,
          borderColor: isSelected ? pill.color : `${pill.color}40`
        }}>
        {pill.label}
        <span className={`rounded-full px-1.5 py-0.5 text-[10px] font-bold ${
          isSelected ? 'bg-white/20 text-white' : 'bg-slate-800 text-slate-300'
        }`}>{count}</span>
      </button>
    );
  };

  return (
    <div className="space-y-8">
      <div className="text-center mb-4">
        <h2 className="text-xl font-bold text-white">Pipeline Justine — Test Comparatif</h2>
        <p className="text-sm text-slate-400 mt-1">Prise R2 + Négo Avance + RAC + Accord Chantier</p>
      </div>

      {/* ══════════════════════════════════════════════════════════ */}
      {/* APPROCHE A — Pills avec expansion                         */}
      {/* ══════════════════════════════════════════════════════════ */}
      <div className="bg-slate-800/70 border border-blue-500/30 rounded-xl p-5" data-testid="justine-approche-a">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-bold text-blue-400">Approche A — Pills avec expansion</h3>
          <button onClick={() => { setSelectedPill(null); setExpandedGroup(null); }}
            className="text-xs text-slate-500 hover:text-white px-2 py-1 rounded bg-slate-700/50">Reset</button>
        </div>
        <p className="text-xs text-slate-400 mb-4">Les groupes sont des pills agrégées. Cliquer sur un groupe déploie ses sous-pills.</p>

        {/* Compact pills row — group pills */}
        <div className="flex flex-wrap gap-2 mb-3">
          {Object.entries(GROUPS).map(([gid, group]) => {
            const total = groupTotal(gid);
            const isExpanded = expandedGroup === gid;
            return (
              <button key={gid} onClick={() => setExpandedGroup(isExpanded ? null : gid)}
                className={`inline-flex items-center gap-2 rounded-full px-4 py-2 text-xs font-bold transition-all ${
                  isExpanded
                    ? 'ring-2 ring-white/20 shadow-lg scale-105'
                    : 'hover:scale-105'
                }`}
                style={{
                  backgroundColor: isExpanded ? group.color : `${group.color}15`,
                  color: isExpanded ? '#fff' : group.color,
                  borderWidth: 1,
                  borderColor: isExpanded ? group.color : `${group.color}30`
                }}>
                {group.label}
                <span className={`rounded-full px-2 py-0.5 text-[10px] font-bold ${
                  isExpanded ? 'bg-white/20 text-white' : 'bg-slate-800 text-slate-300'
                }`}>{total}</span>
                <ChevronRight className={`w-3 h-3 transition-transform ${isExpanded ? 'rotate-90' : ''}`} />
              </button>
            );
          })}
        </div>

        {/* Expanded sub-pills */}
        {expandedGroup && (
          <div className="bg-slate-900/50 rounded-lg border border-slate-700/50 p-3 mb-3">
            <div className="flex flex-wrap gap-2">
              {GROUPS[expandedGroup].pills.map(pill => (
                <PillBadge key={pill.id} pill={pill} isSelected={selectedPill === pill.id}
                  onClick={() => setSelectedPill(selectedPill === pill.id ? null : pill.id)} size="small" />
              ))}
            </div>
          </div>
        )}

        {/* Fake client list */}
        {selectedPill && (
          <div className="bg-slate-900/50 rounded-lg border border-slate-700/50 p-3">
            <p className="text-xs text-slate-400 mb-2">Clients en <span className="text-white font-medium">{
              Object.values(GROUPS).flatMap(g => g.pills).find(p => p.id === selectedPill)?.label
            }</span> :</p>
            {Array.from({ length: FAKE[selectedPill] || 0 }, (_, i) => (
              <div key={i} className="flex items-center gap-3 py-2 border-b border-slate-800 last:border-0">
                <div className="w-7 h-7 rounded-full bg-slate-700 flex items-center justify-center text-[10px] text-slate-400 font-bold">{i + 1}</div>
                <div>
                  <p className="text-sm text-white font-medium">Client Fictif {i + 1}</p>
                  <p className="text-[10px] text-slate-500">06 XX XX XX XX</p>
                </div>
              </div>
            ))}
            {(FAKE[selectedPill] || 0) === 0 && <p className="text-xs text-slate-500 italic">Aucun client</p>}
          </div>
        )}
      </div>

      {/* ══════════════════════════════════════════════════════════ */}
      {/* APPROCHE B — Pills flat avec séparateurs                  */}
      {/* ══════════════════════════════════════════════════════════ */}
      <div className="bg-slate-800/70 border border-emerald-500/30 rounded-xl p-5" data-testid="justine-approche-b">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-bold text-emerald-400">Approche B — Pills flat avec séparateurs</h3>
          <button onClick={() => setSelectedPill(null)}
            className="text-xs text-slate-500 hover:text-white px-2 py-1 rounded bg-slate-700/50">Reset</button>
        </div>
        <p className="text-xs text-slate-400 mb-4">Toutes les pills sont visibles, séparées par des headers de groupe.</p>

        <div className="space-y-4">
          {Object.entries(GROUPS).map(([gid, group]) => (
            <div key={gid}>
              <div className="flex items-center gap-2 mb-2">
                <div className="w-2 h-2 rounded-full" style={{ backgroundColor: group.color }} />
                <span className="text-[10px] uppercase tracking-wider font-bold" style={{ color: group.color }}>{group.label}</span>
                <span className="text-[10px] text-slate-500">({groupTotal(gid)})</span>
                <div className="flex-1 h-px" style={{ backgroundColor: `${group.color}20` }} />
              </div>
              <div className="flex flex-wrap gap-2">
                {group.pills.map(pill => (
                  <PillBadge key={pill.id} pill={pill} isSelected={selectedPill === pill.id}
                    onClick={() => setSelectedPill(selectedPill === pill.id ? null : pill.id)} />
                ))}
              </div>
            </div>
          ))}
        </div>

        {/* Fake client list */}
        {selectedPill && (
          <div className="bg-slate-900/50 rounded-lg border border-slate-700/50 p-3 mt-4">
            <p className="text-xs text-slate-400 mb-2">Clients en <span className="text-white font-medium">{
              Object.values(GROUPS).flatMap(g => g.pills).find(p => p.id === selectedPill)?.label
            }</span> :</p>
            {Array.from({ length: FAKE[selectedPill] || 0 }, (_, i) => (
              <div key={i} className="flex items-center gap-3 py-2 border-b border-slate-800 last:border-0">
                <div className="w-7 h-7 rounded-full bg-slate-700 flex items-center justify-center text-[10px] text-slate-400 font-bold">{i + 1}</div>
                <div>
                  <p className="text-sm text-white font-medium">Client Fictif {i + 1}</p>
                  <p className="text-[10px] text-slate-500">06 XX XX XX XX</p>
                </div>
              </div>
            ))}
            {(FAKE[selectedPill] || 0) === 0 && <p className="text-xs text-slate-500 italic">Aucun client</p>}
          </div>
        )}
      </div>

      {/* Comparison */}
      <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
        <div className="bg-blue-500/5 border border-blue-500/20 rounded-xl p-4">
          <h4 className="text-sm font-bold text-blue-400 mb-2">Approche A — Résumé</h4>
          <ul className="text-xs text-slate-400 space-y-1">
            <li>+ Compact : 4 pills visibles par défaut</li>
            <li>+ Vue d'ensemble rapide avec compteurs agrégés</li>
            <li>+ Drill-down quand on veut le détail</li>
            <li>- 1 clic de plus pour voir les sous-statuts</li>
          </ul>
        </div>
        <div className="bg-emerald-500/5 border border-emerald-500/20 rounded-xl p-4">
          <h4 className="text-sm font-bold text-emerald-400 mb-2">Approche B — Résumé</h4>
          <ul className="text-xs text-slate-400 space-y-1">
            <li>+ Tout visible d'un coup, pas de clic supplémentaire</li>
            <li>+ Headers de groupe pour se repérer</li>
            <li>- Plus de place à l'écran</li>
            <li>- Beaucoup de pills quand il y a beaucoup d'étapes</li>
          </ul>
        </div>
      </div>
    </div>
  );
}


// ═══════════════════════════════════════════════════════════════
// CONFIG NEW TEST — Maquette config pipeline avec groupe_pipeline
// ═══════════════════════════════════════════════════════════════
function ConfigNewTest() {
  // Fake groups
  const [groups, setGroups] = useState([
    { id: "prise_r1", label: "Prise R1", color: "#3B82F6", ordre: 1 },
    { id: "prise_r2", label: "Prise R2", color: "#8B5CF6", ordre: 2 },
    { id: "nego_avance", label: "Négo Avance", color: "#F59E0B", ordre: 3 },
    { id: "rac", label: "RAC", color: "#A855F7", ordre: 4 },
    { id: "devis_anah", label: "Devis ANAH", color: "#06B6D4", ordre: 5 },
    { id: "dc_materiaux", label: "DC & Matériaux", color: "#EC4899", ordre: 6 },
    { id: "chantier", label: "Chantier", color: "#10B981", ordre: 7 },
  ]);

  const [newGroupLabel, setNewGroupLabel] = useState("");

  // Fake pipeline etapes with groupe_pipeline field
  const [etapes, setEtapes] = useState([
    { id: "lead", nom: "Lead", categorie: "avant_r1", groupe_pipeline: "", dashboards: ["telepro"] },
    { id: "lead_hz", nom: "Lead Hors Zone", categorie: "avant_r1", groupe_pipeline: "", dashboards: ["telepro"] },
    { id: "r1_planifie", nom: "R1 Planifié", categorie: "avant_r1", groupe_pipeline: "prise_r1", dashboards: ["telepro"] },
    { id: "r1_nrp", nom: "R1 NRP", categorie: "avant_r1", groupe_pipeline: "prise_r1", dashboards: ["telepro"] },
    { id: "r1_a_rappeler", nom: "R1 à rappeler", categorie: "avant_r1", groupe_pipeline: "prise_r1", dashboards: ["telepro"] },
    { id: "r1_realise", nom: "R1 Réalisé", categorie: "apres_r1", groupe_pipeline: "prise_r1", dashboards: ["telepro", "archi"] },
    { id: "vt_amo_planifiee", nom: "VT AMO Planifiée", categorie: "apres_r1", groupe_pipeline: "", dashboards: ["archi"] },
    { id: "vt_amo_realisee", nom: "VT AMO Réalisée", categorie: "apres_r1", groupe_pipeline: "", dashboards: ["archi"] },
    { id: "r2_a_planifier", nom: "R2 à planifier", categorie: "r2_archi", groupe_pipeline: "prise_r2", dashboards: ["justine"] },
    { id: "r2_nrp", nom: "R2 NRP", categorie: "r2_archi", groupe_pipeline: "prise_r2", dashboards: ["justine"] },
    { id: "r2_a_rappeler", nom: "R2 à rappeler", categorie: "r2_archi", groupe_pipeline: "prise_r2", dashboards: ["justine"] },
    { id: "r2_planifie", nom: "R2 Planifié", categorie: "r2_archi", groupe_pipeline: "prise_r2", dashboards: ["justine"] },
    { id: "r2_realise", nom: "R2 Réalisé", categorie: "r2_archi", groupe_pipeline: "prise_r2", dashboards: ["justine", "archi"] },
    { id: "devis_anah_valide", nom: "Devis ANAH validé", categorie: "r2_archi", groupe_pipeline: "devis_anah", dashboards: ["archi"] },
    { id: "devis_anah_a_modifier", nom: "Devis ANAH à modifier", categorie: "r2_archi", groupe_pipeline: "devis_anah", dashboards: ["archi"] },
    { id: "depot_anah_autorise", nom: "Dépôt ANAH autorisé", categorie: "r2_archi", groupe_pipeline: "devis_anah", dashboards: ["archi"] },
    { id: "sonder", nom: "Sonder le client", categorie: "r2_archi", groupe_pipeline: "nego_avance", dashboards: ["justine"] },
    { id: "nego_client", nom: "Négo client", categorie: "r2_archi", groupe_pipeline: "nego_avance", dashboards: ["justine"] },
    { id: "client_30", nom: "Client 30%", categorie: "r2_archi", groupe_pipeline: "nego_avance", dashboards: ["justine"] },
    { id: "anah_30_demande", nom: "ANAH 30% - Demande AMO", categorie: "r2_archi", groupe_pipeline: "nego_avance", dashboards: ["justine"] },
    { id: "anah_30_en_cours", nom: "ANAH 30% - En cours", categorie: "r2_archi", groupe_pipeline: "nego_avance", dashboards: ["justine"] },
    { id: "avance_recue", nom: "Avance reçue Sani", categorie: "r2_archi", groupe_pipeline: "nego_avance", dashboards: ["justine", "compta"] },
    { id: "rac_cheque", nom: "RAC chèque déposé", categorie: "compta_rac", groupe_pipeline: "rac", dashboards: ["justine", "compta"] },
    { id: "rac_echelonne", nom: "RAC échelonné", categorie: "compta_rac", groupe_pipeline: "rac", dashboards: ["justine", "compta"] },
    { id: "rac_solde", nom: "Soldé", categorie: "compta_rac", groupe_pipeline: "rac", dashboards: ["compta"] },
    { id: "dc_a_faire", nom: "DC à faire", categorie: "archi_dc_mat", groupe_pipeline: "dc_materiaux", dashboards: ["archi"] },
    { id: "materiaux_a_valider", nom: "Matériaux à valider", categorie: "archi_dc_mat", groupe_pipeline: "dc_materiaux", dashboards: ["archi"] },
    { id: "accord_chantier", nom: "Accord chantier", categorie: "r2_archi", groupe_pipeline: "chantier", dashboards: ["justine", "archi"] },
  ]);

  const [editingEtape, setEditingEtape] = useState(null);
  const [previewDashboard, setPreviewDashboard] = useState("justine");
  const [editingGroupId, setEditingGroupId] = useState(null);
  const [editingGroupLabel, setEditingGroupLabel] = useState("");

  const updateEtapeGroupe = (etapeId, groupeId) => {
    setEtapes(prev => prev.map(e => e.id === etapeId ? { ...e, groupe_pipeline: groupeId } : e));
  };

  const addGroup = () => {
    if (!newGroupLabel.trim()) return;
    const id = newGroupLabel.trim().toLowerCase().replace(/\s+/g, '_').replace(/[^a-z0-9_]/g, '');
    const colors = ['#EF4444', '#F59E0B', '#10B981', '#3B82F6', '#8B5CF6', '#EC4899', '#06B6D4'];
    setGroups(prev => [...prev, { id, label: newGroupLabel.trim(), color: colors[prev.length % colors.length], ordre: prev.length + 1 }]);
    setNewGroupLabel("");
  };

  const renameGroup = (gid) => {
    if (!editingGroupLabel.trim()) { setEditingGroupId(null); return; }
    setGroups(prev => prev.map(g => g.id === gid ? { ...g, label: editingGroupLabel.trim() } : g));
    setEditingGroupId(null);
    setEditingGroupLabel("");
  };

  const deleteGroup = (gid) => {
    const g = groups.find(gr => gr.id === gid);
    const count = etapes.filter(e => e.groupe_pipeline === gid).length;
    if (!window.confirm(`Supprimer le groupe "${g?.label}" ? ${count > 0 ? `${count} étape(s) passeront en "Non groupé".` : ''}`)) return;
    setGroups(prev => prev.filter(gr => gr.id !== gid));
    setEtapes(prev => prev.map(e => e.groupe_pipeline === gid ? { ...e, groupe_pipeline: "" } : e));
    if (editingGroupId === gid) setEditingGroupId(null);
  };

  // Preview: group etapes by groupe_pipeline
  const previewGroups = {};
  const ungrouped = [];
  etapes.forEach(e => {
    if (e.groupe_pipeline) {
      if (!previewGroups[e.groupe_pipeline]) previewGroups[e.groupe_pipeline] = [];
      previewGroups[e.groupe_pipeline].push(e);
    } else {
      ungrouped.push(e);
    }
  });

  const groupMap = {};
  groups.forEach(g => { groupMap[g.id] = g; });

  return (
    <div className="space-y-6">
      <div className="text-center mb-4">
        <h2 className="text-xl font-bold text-white">Config Pipeline — Nouveau champ "Groupe Pipeline"</h2>
        <p className="text-sm text-slate-400 mt-1">Chaque étape peut être assignée à un groupe pour organiser les pills dans le dashboard</p>
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
        {/* LEFT: Config panel */}
        <div className="space-y-4">
          {/* Groupe management */}
          <div className="bg-slate-800/70 border border-violet-500/30 rounded-xl p-4">
            <h3 className="text-sm font-bold text-violet-400 mb-3">Groupes Pipeline</h3>
            <div className="flex flex-wrap gap-2 mb-3">
              {groups.map(g => (
                <div key={g.id} className="relative group/pill inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-xs font-medium"
                  style={{ backgroundColor: `${g.color}20`, color: g.color, border: `1px solid ${g.color}40` }}>
                  <div className="w-2 h-2 rounded-full" style={{ backgroundColor: g.color }} />
                  {editingGroupId === g.id ? (
                    <input type="text" value={editingGroupLabel} onChange={e => setEditingGroupLabel(e.target.value)}
                      onKeyDown={e => { if (e.key === 'Enter') renameGroup(g.id); if (e.key === 'Escape') setEditingGroupId(null); }}
                      onBlur={() => renameGroup(g.id)} autoFocus
                      className="bg-transparent border-b border-current outline-none text-xs w-20 font-medium" style={{ color: g.color }} />
                  ) : (
                    <span>{g.label}</span>
                  )}
                  {editingGroupId !== g.id && (
                    <div className="hidden group-hover/pill:flex items-center gap-0.5 ml-1">
                      <button onClick={() => { setEditingGroupId(g.id); setEditingGroupLabel(g.label); }}
                        className="w-4 h-4 rounded-full flex items-center justify-center hover:bg-white/10 transition-colors" title="Renommer">
                        <svg className="w-2.5 h-2.5" viewBox="0 0 16 16" fill="currentColor"><path d="M12.1 1.9a2.1 2.1 0 0 1 3 3L5.3 14.7l-4 .9.9-4L12.1 1.9z"/></svg>
                      </button>
                      <button onClick={() => deleteGroup(g.id)}
                        className="w-4 h-4 rounded-full flex items-center justify-center hover:bg-red-500/20 text-red-400 transition-colors" title="Supprimer">
                        <X className="w-2.5 h-2.5" />
                      </button>
                    </div>
                  )}
                </div>
              ))}
            </div>
            <div className="flex gap-2">
              <input type="text" value={newGroupLabel} onChange={e => setNewGroupLabel(e.target.value)}
                placeholder="Nouveau groupe..." onKeyDown={e => e.key === 'Enter' && addGroup()}
                className="flex-1 bg-slate-900 border border-slate-600 rounded-lg px-3 py-1.5 text-sm text-white placeholder:text-slate-500 outline-none focus:border-violet-500" />
              <button onClick={addGroup} className="px-3 py-1.5 bg-violet-600 text-white text-xs rounded-lg font-medium hover:bg-violet-500">Ajouter</button>
            </div>
          </div>

          {/* Etapes list with groupe_pipeline dropdown */}
          <div className="bg-slate-800/70 border border-slate-700/50 rounded-xl p-4 max-h-[500px] overflow-y-auto">
            <h3 className="text-sm font-bold text-slate-300 mb-3">Étapes Pipeline — Champ "Groupe"</h3>
            <div className="space-y-1.5">
              {etapes.map(e => (
                <div key={e.id} className="flex items-center gap-3 py-1.5 px-2 rounded-lg hover:bg-slate-700/30 group">
                  <div className="flex-1 min-w-0">
                    <span className="text-xs text-white font-medium truncate block">{e.nom}</span>
                    <span className="text-[10px] text-slate-500">{e.categorie}</span>
                  </div>
                  <select value={e.groupe_pipeline} onChange={ev => updateEtapeGroupe(e.id, ev.target.value)}
                    className="bg-slate-900 border border-slate-600 rounded px-2 py-1 text-[11px] text-white outline-none focus:border-violet-500 w-40"
                    style={e.groupe_pipeline && groupMap[e.groupe_pipeline] ? { borderColor: `${groupMap[e.groupe_pipeline].color}60` } : {}}>
                    <option value="">— Aucun groupe —</option>
                    {groups.map(g => (
                      <option key={g.id} value={g.id}>{g.label}</option>
                    ))}
                  </select>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* RIGHT: Live preview */}
        <div className="space-y-4">
          <div className="bg-slate-800/70 border border-emerald-500/30 rounded-xl p-4">
            <h3 className="text-sm font-bold text-emerald-400 mb-3">Aperçu Dashboard — Approche A (pills avec expansion)</h3>

            {/* Dashboard selector */}
            <div className="flex flex-wrap gap-1.5 mb-4">
              {[
                { id: "all", label: "Tous", color: "#64748b" },
                { id: "telepro", label: "Télépro", color: "#3B82F6" },
                { id: "justine", label: "Justine", color: "#EC4899" },
                { id: "archi", label: "Archi", color: "#10B981" },
                { id: "compta", label: "Compta", color: "#F59E0B" },
              ].map(d => (
                <button key={d.id} onClick={() => setPreviewDashboard(d.id)}
                  className={`px-3 py-1.5 rounded-full text-xs font-bold transition-all ${
                    previewDashboard === d.id ? 'scale-105 shadow-md' : 'hover:scale-105'
                  }`}
                  style={{
                    backgroundColor: previewDashboard === d.id ? d.color : `${d.color}15`,
                    color: previewDashboard === d.id ? '#fff' : d.color,
                    border: `1px solid ${previewDashboard === d.id ? d.color : d.color + '30'}`
                  }}>
                  {d.label}
                </button>
              ))}
            </div>

            {/* Grouped pills preview filtered by dashboard */}
            <div className="space-y-3">
              {groups.filter(g => {
                const pills = (previewGroups[g.id] || []).filter(e =>
                  previewDashboard === "all" || (e.dashboards || []).includes(previewDashboard)
                );
                return pills.length > 0;
              }).map(g => {
                const pills = (previewGroups[g.id] || []).filter(e =>
                  previewDashboard === "all" || (e.dashboards || []).includes(previewDashboard)
                );
                return (
                  <div key={g.id}>
                    <button onClick={() => setEditingEtape(editingEtape === g.id ? null : g.id)}
                      className={`inline-flex items-center gap-2 rounded-full px-4 py-2 text-xs font-bold transition-all ${
                        editingEtape === g.id ? 'ring-2 ring-white/20 shadow-lg scale-105' : 'hover:scale-105'
                      }`}
                      style={{
                        backgroundColor: editingEtape === g.id ? g.color : `${g.color}15`,
                        color: editingEtape === g.id ? '#fff' : g.color,
                        border: `1px solid ${editingEtape === g.id ? g.color : g.color + '30'}`
                      }}>
                      {g.label}
                      <span className={`rounded-full px-2 py-0.5 text-[10px] font-bold ${
                        editingEtape === g.id ? 'bg-white/20 text-white' : 'bg-slate-800 text-slate-300'
                      }`}>{pills.length}</span>
                      <ChevronRight className={`w-3 h-3 transition-transform ${editingEtape === g.id ? 'rotate-90' : ''}`} />
                    </button>
                    {editingEtape === g.id && (
                      <div className="mt-2 ml-4 bg-slate-900/50 rounded-lg border border-slate-700/50 p-2">
                        <div className="flex flex-wrap gap-1.5">
                          {pills.map(p => (
                            <span key={p.id} className="inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-[10px] font-medium"
                              style={{ backgroundColor: `${g.color}20`, color: g.color, border: `1px solid ${g.color}40` }}>
                              {p.nom}
                              <span className="rounded-full px-1 py-0.5 bg-slate-800 text-slate-300 text-[9px]">{Math.floor(Math.random() * 5)}</span>
                            </span>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}

              {/* Ungrouped filtered by dashboard */}
              {(() => {
                const filteredUngrouped = ungrouped.filter(e =>
                  previewDashboard === "all" || (e.dashboards || []).includes(previewDashboard)
                );
                return filteredUngrouped.length > 0 && (
                  <div>
                    <div className="flex items-center gap-2 mb-1">
                      <div className="w-2 h-2 rounded-full bg-slate-500" />
                      <span className="text-[10px] uppercase tracking-wider text-slate-500 font-bold">Non groupé ({filteredUngrouped.length})</span>
                    </div>
                    <div className="flex flex-wrap gap-1.5 ml-4">
                      {filteredUngrouped.map(p => (
                        <span key={p.id} className="inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-[10px] font-medium bg-slate-700/30 text-slate-400 border border-slate-600/50">
                          {p.nom}
                        </span>
                      ))}
                    </div>
                  </div>
                );
              })()}

              {/* Empty state */}
              {groups.every(g => !(previewGroups[g.id] || []).some(e =>
                previewDashboard === "all" || (e.dashboards || []).includes(previewDashboard)
              )) && ungrouped.filter(e => previewDashboard === "all" || (e.dashboards || []).includes(previewDashboard)).length === 0 && (
                <p className="text-xs text-slate-500 italic">Aucune étape assignée à ce dashboard</p>
              )}
            </div>
          </div>

          {/* Info box */}
          <div className="bg-violet-500/5 border border-violet-500/20 rounded-xl p-4">
            <h4 className="text-sm font-bold text-violet-400 mb-2">Comment ça marche</h4>
            <ul className="text-xs text-slate-400 space-y-1.5">
              <li><strong className="text-white">1.</strong> Créer les groupes pipeline (colonne gauche, en haut)</li>
              <li><strong className="text-white">2.</strong> Assigner chaque étape à un groupe via le dropdown</li>
              <li><strong className="text-white">3.</strong> L'aperçu à droite montre le rendu pills en temps réel</li>
              <li><strong className="text-white">4.</strong> Les étapes sans groupe apparaissent en "Non groupé"</li>
              <li className="mt-2 text-violet-300">Le champ <code className="bg-slate-800 px-1 rounded">groupe_pipeline</code> serait ajouté dans la config de chaque étape, à côté de "catégorie" et "dashboards".</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
}


// ═══════════════════════════════════════════════════════════════
export default function TestsDashboard({ onBack, authUser, onNavigate, onMonEspaceClick, onCalendrierClick, onAddDevis, onAddLead }) {
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [selectedClient, setSelectedClient] = useState(null);
  const [stats, setStats] = useState({ total: 0, loaded: 0 });
  const [filterStatus, setFilterStatus] = useState("");
  const [showFilters, setShowFilters] = useState(false);
  const [showSaniaConnect, setShowSaniaConnect] = useState(false);
  const [activeTab, setActiveTab] = useState("clients");
  const [sidebarConfig, setSidebarConfig] = useState(null);
  const [bannersFooterConfig, setBannersFooterConfig] = useState(null);

  // Load dashboard config (sidebar + banners_footer)
  useEffect(() => {
    (async () => {
      try {
        const resp = await authFetch(`${API}/config/dashboard/tests`);
        if (resp.ok) {
          const data = await resp.json();
          setSidebarConfig(data.sidebar || null);
          setBannersFooterConfig(data.banners_footer || null);
        }
      } catch (e) { /* use defaults */ }
    })();
  }, []);

  const isSidebarEnabled = (itemId) => {
    if (!sidebarConfig) return true;
    const item = sidebarConfig.find(i => i.id === itemId);
    return item ? item.enabled : true;
  };

  const isBannerFooterEnabled = (itemId) => {
    if (!bannersFooterConfig) return true;
    const item = bannersFooterConfig.find(i => i.id === itemId);
    return item ? item.enabled : true;
  };

  const hasSidebar = !sidebarConfig || sidebarConfig.some(i => i.enabled);

  // Charger les clients
  const loadClients = useCallback(async () => {
    setLoading(true);
    try {
      const resp = await authFetch(`${API}/pipeline/clients`);
      const data = await resp.json();
      setClients(data.clients || []);
      setStats({ total: data.total || 0, loaded: data.clients?.length || 0 });
    } catch (e) {
      console.error("Erreur chargement clients:", e);
      toast.error("Erreur lors du chargement des clients");
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    loadClients();
  }, [loadClients]);

  // Filtrer les clients
  const filteredClients = clients.filter(client => {
    const matchSearch = !search || 
      (client.nom || '').toLowerCase().includes(search.toLowerCase()) ||
      (client.prenom || '').toLowerCase().includes(search.toLowerCase()) ||
      (client.id_qhare || '').toLowerCase().includes(search.toLowerCase()) ||
      (client.client_id || '').toLowerCase().includes(search.toLowerCase());
    
    const matchStatus = !filterStatus || client.statut === filterStatus;
    
    return matchSearch && matchStatus;
  });

  // Ouvrir le modal d'un client
  const handleClientClick = async (client) => {
    try {
      const resp = await authFetch(`${API}/pipeline/client/by-row/${client.row_number}`);
      const fullData = await resp.json();
      setSelectedClient({
        ...client,
        ...fullData,
        data: {
          'CLIENT NOM': client.nom,
          'PRÉNOM': client.prenom,
          'ID QHARE': client.id_qhare
        }
      });
    } catch (e) {
      console.error("Erreur chargement client:", e);
      setSelectedClient({
        ...client,
        data: {
          'CLIENT NOM': client.nom,
          'PRÉNOM': client.prenom,
          'ID QHARE': client.id_qhare
        }
      });
    }
  };

  const uniqueStatuses = [...new Set(clients.map(c => c.statut).filter(Boolean))];

  // Si SAN-IA Connect est actif, afficher uniquement ce composant
  if (showSaniaConnect) {
    return <SaniaConnect onBack={() => setShowSaniaConnect(false)} />;
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900">
      {/* Unified Header — Prototype */}
      <UnifiedHeader
        current="tests"
        onNavigate={onNavigate || onBack}
        authUser={authUser}
        onMonEspaceClick={onMonEspaceClick}
        onCalendrierClick={onCalendrierClick}
        onRefresh={loadClients}
        refreshing={loading}
        onAddLead={onAddLead}
        onAddDevis={onAddDevis}
      />

      {/* KPIs Bar - like Telepro SanIABanner */}
      <div className="px-6 pt-4 pb-2">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <div className="bg-slate-800/60 border border-slate-700/50 rounded-xl p-3">
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 rounded-lg bg-blue-500/20 flex items-center justify-center">
                <Users className="w-4 h-4 text-blue-400" />
              </div>
              <div>
                <p className="text-lg font-bold text-white">{stats.total}</p>
                <p className="text-xs text-slate-400">Clients total</p>
              </div>
            </div>
          </div>
          <div className="bg-slate-800/60 border border-slate-700/50 rounded-xl p-3">
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 rounded-lg bg-emerald-500/20 flex items-center justify-center">
                <Eye className="w-4 h-4 text-emerald-400" />
              </div>
              <div>
                <p className="text-lg font-bold text-white">{filteredClients.length}</p>
                <p className="text-xs text-slate-400">Affichés</p>
              </div>
            </div>
          </div>
          <div className="bg-slate-800/60 border border-slate-700/50 rounded-xl p-3">
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 rounded-lg bg-purple-500/20 flex items-center justify-center">
                <Layers className="w-4 h-4 text-purple-400" />
              </div>
              <div>
                <p className="text-lg font-bold text-white">{uniqueStatuses.length}</p>
                <p className="text-xs text-slate-400">Statuts</p>
              </div>
            </div>
          </div>
          <div className="bg-slate-800/60 border border-slate-700/50 rounded-xl p-3">
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 rounded-lg bg-orange-500/20 flex items-center justify-center">
                <Calendar className="w-4 h-4 text-orange-400" />
              </div>
              <div>
                <p className="text-lg font-bold text-white">{clients.filter(c => c.date_rdv).length}</p>
                <p className="text-xs text-slate-400">Avec RDV</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Main Grid Layout - like Telepro */}
      <div className="p-6 pt-4">
        <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
          {/* Sidebar gauche (col 1) */}
          <div className="space-y-4" data-testid="tests-sidebar">
            {isSidebarEnabled("sb_mes_rappels") && (
              <MesRappelsWidget authUser={authUser} onAppelerLead={(lead) => console.log('Appeler rappel:', lead)} />
            )}
            {isSidebarEnabled("sb_visa_ia") && (
              <VisaIAWidget authUser={authUser} />
            )}
            {isSidebarEnabled("sb_communication_interne") && (
              <CommunicationInterneWidget authUser={authUser} />
            )}
            {isSidebarEnabled("sb_ma_todo_list") && (
              <MaTodoListWidget authUser={authUser} />
            )}
            {isSidebarEnabled("sb_filtre_telepros") && (
              <div className="bg-slate-800/50 border border-slate-700/50 rounded-xl p-4">
                <h4 className="text-sm font-semibold text-white flex items-center gap-2 mb-3">
                  <Users className="w-4 h-4 text-indigo-400" /> Filtre Telepros
                </h4>
                <p className="text-xs text-slate-500">Filtrage par teleprospecteur — a brancher</p>
              </div>
            )}
            {isSidebarEnabled("sb_stats_ringover") && (
              <div className="bg-slate-800/50 border border-slate-700/50 rounded-xl p-4">
                <h4 className="text-sm font-semibold text-white flex items-center gap-2 mb-3">
                  <BarChart2 className="w-4 h-4 text-teal-400" /> Stats Ringover
                </h4>
                <p className="text-xs text-slate-500">Statistiques d'appels — a brancher</p>
              </div>
            )}
          </div>

          {/* Contenu principal (col-span-3) */}
          <div className="lg:col-span-3 space-y-4">

      {/* SAN-IA Connect Banner - NEW MODULE */}
      <div
          onClick={() => setShowSaniaConnect(true)}
          className="bg-gradient-to-r from-emerald-500/20 via-purple-500/20 to-pink-500/20 border border-emerald-500/30 rounded-xl p-4 cursor-pointer hover:border-emerald-400/50 transition-all group"
        >
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-emerald-500 via-purple-500 to-pink-500 flex items-center justify-center shadow-lg group-hover:scale-105 transition-transform">
                <MessageSquare className="w-7 h-7 text-white" />
              </div>
              <div>
                <h3 className="text-xl font-bold text-white flex items-center gap-2">
                  SAN-IA Connect
                  <span className="px-2 py-0.5 bg-emerald-500 text-white text-xs rounded-full">NEW</span>
                </h3>
                <p className="text-slate-400 mt-1">
                  Hub de communication collaborateurs • WhatsApp • SMS • Vocal • Appels Ringover
                </p>
                <div className="flex items-center gap-3 mt-2">
                  <span className="text-xs bg-purple-500/20 text-purple-300 px-2 py-1 rounded-full">🤖 IA Christelle intégrée</span>
                  <span className="text-xs bg-yellow-500/20 text-yellow-300 px-2 py-1 rounded-full">🏆 Gamification</span>
                  <span className="text-xs bg-blue-500/20 text-blue-300 px-2 py-1 rounded-full">🎤 Messages vocaux</span>
                </div>
              </div>
            </div>
            <ChevronRight className="w-6 h-6 text-slate-400 group-hover:text-white group-hover:translate-x-1 transition-all" />
          </div>
        </div>

      {/* Info Banner */}
        <div className="bg-gradient-to-r from-purple-500/10 to-pink-500/10 border border-purple-500/30 rounded-xl p-4">
          <div className="flex items-start gap-3">
            <Info className="w-5 h-5 text-purple-400 mt-0.5" />
            <div>
              <h3 className="font-medium text-white">Mode Prévisualisation</h3>
              <p className="text-sm text-slate-400 mt-1">
                Ce dashboard permet de prévisualiser les modales clients avec la configuration actuelle.
                Pour modifier la configuration, allez dans <span className="text-purple-400">Super Batard &gt; Champs Fiche Client</span>.
              </p>
            </div>
          </div>
        </div>

      {/* TEST RINGOVER */}
        <div className="bg-slate-800 border border-slate-700 rounded-xl p-4">
          <h3 className="font-medium text-white mb-4 flex items-center gap-2">
            <Phone className="w-5 h-5 text-emerald-400" />
            Test Ringover - Numéros de téléphone
          </h3>
          <div className="space-y-3 text-white">
            <p>1. Texte brut : 06 12 12 12 12</p>
            <p>2. Texte brut sans espaces : 0612121212</p>
            <p>3. Format international : +33612121212</p>
            <p>4. Format avec points : 06.12.12.12.12</p>
            <p>5. Lien tel : <a href="tel:+33612121212" style={{color: '#10b981'}}>06 12 12 12 12</a></p>
            <p>6. Lien tel avec data-phone : <a href="tel:+33612121212" data-phone="+33612121212" style={{color: '#10b981'}}>06 12 12 12 12</a></p>
          </div>
          {/* Option 2: Force DOM mutation pour Ringover */}
          <div 
            className="mt-4 p-3 bg-emerald-500/20 border border-emerald-500/50 rounded-lg"
            ref={(el) => {
              if (el && !el.dataset.ringoverForced) {
                el.dataset.ringoverForced = 'true';
                // Créer le contenu après un délai pour forcer une mutation visible
                setTimeout(() => {
                  const span = document.createElement('span');
                  span.className = 'text-emerald-400';
                  span.textContent = '7. DOM Mutation forcée : +33612121212';
                  el.appendChild(span);
                  // Forcer une autre mutation pour Ringover
                  setTimeout(() => {
                    const clone = span.cloneNode(true);
                    el.replaceChild(clone, span);
                  }, 100);
                }, 500);
              }
            }}
          />
          {/* Option 3: Test avec iframe pour isolation */}
          <div className="mt-4 p-3 bg-blue-500/20 border border-blue-500/50 rounded-lg">
            <p className="text-blue-400 mb-2">8. Via iframe (isolation du DOM React) :</p>
            <iframe 
              srcDoc='<!DOCTYPE html><html><body style="background:transparent;color:#10b981;font-family:system-ui;margin:0;padding:0;">+33612121212</body></html>'
              style={{border: 'none', background: 'transparent', width: '200px', height: '24px'}}
              title="Phone number for Ringover"
            />
          </div>
        </div>

      {/* Tabs */}
        <div className="flex flex-wrap gap-2">
          <button
            data-testid="tab-predeploy"
            onClick={() => setActiveTab("predeploy")}
            className={`px-4 py-2 rounded-lg text-sm font-medium flex items-center gap-2 transition-colors ${activeTab === "predeploy" ? "bg-emerald-600 text-white" : "bg-slate-800 text-slate-400 hover:bg-slate-700"}`}
          >
            <Shield className="w-4 h-4" /> Pre-Deploy
          </button>
          <button
            data-testid="tab-clients"
            onClick={() => setActiveTab("clients")}
            className={`px-4 py-2 rounded-lg text-sm font-medium flex items-center gap-2 transition-colors ${activeTab === "clients" ? "bg-purple-600 text-white" : "bg-slate-800 text-slate-400 hover:bg-slate-700"}`}
          >
            <Users className="w-4 h-4" /> Clients ({stats.total})
          </button>
          <button
            data-testid="tab-doublons"
            onClick={() => setActiveTab("doublons")}
            className={`px-4 py-2 rounded-lg text-sm font-medium flex items-center gap-2 transition-colors ${activeTab === "doublons" ? "bg-amber-600 text-white" : "bg-slate-800 text-slate-400 hover:bg-slate-700"}`}
          >
            <Copy className="w-4 h-4" /> Nettoyage Doublons
          </button>
          <button
            data-testid="tab-audit-tenant"
            onClick={() => setActiveTab("audit-tenant")}
            className={`px-4 py-2 rounded-lg text-sm font-medium flex items-center gap-2 transition-colors ${activeTab === "audit-tenant" ? "bg-cyan-600 text-white" : "bg-slate-800 text-slate-400 hover:bg-slate-700"}`}
          >
            <Building2 className="w-4 h-4" /> Audit Tenant
          </button>
          <button
            data-testid="tab-fix-orphans"
            onClick={() => setActiveTab("fix-orphans")}
            className={`px-4 py-2 rounded-lg text-sm font-medium flex items-center gap-2 transition-colors ${activeTab === "fix-orphans" ? "bg-amber-600 text-white" : "bg-slate-800 text-slate-400 hover:bg-slate-700"}`}
          >
            <AlertTriangle className="w-4 h-4" /> Fix Tenant
          </button>
          <button
            data-testid="tab-diag-console"
            onClick={() => setActiveTab("diag-console")}
            className={`px-4 py-2 rounded-lg text-sm font-medium flex items-center gap-2 transition-colors ${activeTab === "diag-console" ? "bg-cyan-600 text-white" : "bg-slate-800 text-slate-400 hover:bg-slate-700"}`}
          >
            <Shield className="w-4 h-4" /> Console Diag
          </button>
          <button
            data-testid="tab-nego-pipeline"
            onClick={() => setActiveTab("nego-pipeline")}
            className={`px-4 py-2 rounded-lg text-sm font-medium flex items-center gap-2 transition-colors ${activeTab === "nego-pipeline" ? "bg-orange-600 text-white" : "bg-slate-800 text-slate-400 hover:bg-slate-700"}`}
          >
            <GitBranch className="w-4 h-4" /> Négo Pipeline
          </button>
          <button
            data-testid="tab-nego-justine"
            onClick={() => setActiveTab("nego-justine")}
            className={`px-4 py-2 rounded-lg text-sm font-medium flex items-center gap-2 transition-colors ${activeTab === "nego-justine" ? "bg-pink-600 text-white" : "bg-slate-800 text-slate-400 hover:bg-slate-700"}`}
          >
            <User className="w-4 h-4" /> Négo Justine Test
          </button>
          <button
            data-testid="tab-config-new"
            onClick={() => setActiveTab("config-new")}
            className={`px-4 py-2 rounded-lg text-sm font-medium flex items-center gap-2 transition-colors ${activeTab === "config-new" ? "bg-violet-600 text-white" : "bg-slate-800 text-slate-400 hover:bg-slate-700"}`}
          >
            <Settings className="w-4 h-4" /> Config New Test
          </button>
        </div>

      {/* Tab: Pre-Deploy Checks */}
      <div
        key="predeploy-panel"
        style={{ display: activeTab === "predeploy" ? "block" : "none" }}
      >
        <div className="bg-slate-800/50 rounded-xl border border-slate-700/50 p-6">
          <PreDeployChecksPanel />
        </div>
      </div>

      {/* Tab: Doublons */}
      <div
        key="dedup-panel"
        style={{ display: activeTab === "doublons" ? "block" : "none" }}
      >
        <div className="bg-slate-800/50 rounded-xl border border-slate-700/50 p-6">
          <DedupPanel />
        </div>
      </div>

      {/* Tab: Audit Tenant */}
      <div
        key="audit-panel"
        style={{ display: activeTab === "audit-tenant" ? "block" : "none" }}
      >
        <div className="bg-slate-800/50 rounded-xl border border-slate-700/50 p-6">
          <AuditTenantPanel />
        </div>
      </div>


      {/* Tab: Fix Tenant Orphans */}
      <div
        key="fix-orphans-panel"
        style={{ display: activeTab === "fix-orphans" ? "block" : "none" }}
      >
        <div className="bg-slate-800/50 rounded-xl border border-slate-700/50 p-6">
          <FixTenantOrphansPanel />
        </div>
      </div>

      {/* Tab: Console Diagnostic */}
      <div
        key="diag-console-panel"
        style={{ display: activeTab === "diag-console" ? "block" : "none" }}
      >
        <div className="bg-slate-800/50 rounded-xl border border-slate-700/50 p-6">
          <DiagConsolePanel />
        </div>
      </div>

      {/* Tab: Négo Pipeline Demo */}
      <div
        key="nego-pipeline-panel"
        style={{ display: activeTab === "nego-pipeline" ? "block" : "none" }}
      >
        <div className="bg-slate-800/50 rounded-xl border border-slate-700/50 p-6">
          <NegoPipelineDemo />
        </div>
      </div>

      {/* Tab: Négo Justine Test */}
      <div
        key="nego-justine-panel"
        style={{ display: activeTab === "nego-justine" ? "block" : "none" }}
      >
        <div className="bg-slate-800/50 rounded-xl border border-slate-700/50 p-6">
          <NegoJustineTest />
        </div>
      </div>

      {/* Tab: Config New Test */}
      <div
        key="config-new-panel"
        style={{ display: activeTab === "config-new" ? "block" : "none" }}
      >
        <div className="bg-slate-800/50 rounded-xl border border-slate-700/50 p-6">
          <ConfigNewTest />
        </div>
      </div>

      {/* Tab: Clients */}
      {activeTab === "clients" && (<>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div className="bg-slate-800/50 rounded-xl p-4 border border-slate-700/50">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-blue-500/20 flex items-center justify-center">
                <Users className="w-5 h-5 text-blue-400" />
              </div>
              <div>
                <p className="text-2xl font-bold text-white">{stats.total}</p>
                <p className="text-xs text-slate-400">Clients total</p>
              </div>
            </div>
          </div>
          <div className="bg-slate-800/50 rounded-xl p-4 border border-slate-700/50">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-emerald-500/20 flex items-center justify-center">
                <Eye className="w-5 h-5 text-emerald-400" />
              </div>
              <div>
                <p className="text-2xl font-bold text-white">{filteredClients.length}</p>
                <p className="text-xs text-slate-400">Affichés</p>
              </div>
            </div>
          </div>
          <div className="bg-slate-800/50 rounded-xl p-4 border border-slate-700/50">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-purple-500/20 flex items-center justify-center">
                <Layers className="w-5 h-5 text-purple-400" />
              </div>
              <div>
                <p className="text-2xl font-bold text-white">{uniqueStatuses.length}</p>
                <p className="text-xs text-slate-400">Statuts</p>
              </div>
            </div>
          </div>
          <div className="bg-slate-800/50 rounded-xl p-4 border border-slate-700/50">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-orange-500/20 flex items-center justify-center">
                <Eye className="w-5 h-5 text-orange-400" />
              </div>
              <div>
                <p className="text-sm font-medium text-white">Prévisualisation</p>
                <p className="text-xs text-slate-400">Lecture seule</p>
              </div>
            </div>
          </div>
        </div>

      {/* Search & Filters */}
        <div className="bg-slate-800/50 rounded-xl p-4 border border-slate-700/50">
          <div className="flex flex-col md:flex-row gap-4">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
              <input
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Rechercher par nom, prénom ou ID..."
                className="w-full bg-slate-700/50 border border-slate-600 rounded-lg pl-10 pr-4 py-2.5 text-white placeholder-slate-500 focus:outline-none focus:border-purple-500"
              />
            </div>
            <button
              onClick={() => setShowFilters(!showFilters)}
              className={`flex items-center gap-2 px-4 py-2.5 rounded-lg transition-colors ${
                showFilters || filterStatus 
                  ? 'bg-purple-500 text-white' 
                  : 'bg-slate-700 text-slate-300 hover:bg-slate-600'
              }`}
            >
              <Filter className="w-4 h-4" />
              Filtres
            </button>
          </div>
          
          {showFilters && (
            <div className="mt-4 pt-4 border-t border-slate-700 flex flex-wrap gap-2">
              <button
                onClick={() => setFilterStatus("")}
                className={`px-3 py-1.5 rounded-lg text-sm ${!filterStatus ? 'bg-purple-500 text-white' : 'bg-slate-700 text-slate-300'}`}
              >
                Tous
              </button>
              {uniqueStatuses.slice(0, 10).map(status => (
                <button
                  key={status}
                  onClick={() => setFilterStatus(status === filterStatus ? "" : status)}
                  className={`px-3 py-1.5 rounded-lg text-sm ${filterStatus === status ? 'bg-purple-500 text-white' : 'bg-slate-700 text-slate-300'}`}
                >
                  {status}
                </button>
              ))}
            </div>
          )}
        </div>

      {/* Client List */}
        <div className="bg-slate-800/50 rounded-xl border border-slate-700/50 overflow-hidden">
          <div className="bg-slate-700/30 px-4 py-3 border-b border-slate-700/50 hidden md:grid grid-cols-12 gap-4 text-sm font-medium text-slate-400">
            <div className="col-span-1">#</div>
            <div className="col-span-3">Client</div>
            <div className="col-span-2">ID Qhare</div>
            <div className="col-span-2">Téléphone</div>
            <div className="col-span-2">Statut</div>
            <div className="col-span-2 text-right">Actions</div>
          </div>

          {loading ? (
            <div className="flex items-center justify-center py-12">
              <RefreshCw className="w-8 h-8 animate-spin text-purple-500" />
            </div>
          ) : filteredClients.length === 0 ? (
            <div className="text-center py-12">
              <Users className="w-12 h-12 mx-auto mb-3 text-slate-600" />
              <p className="text-slate-400">Aucun client trouvé</p>
            </div>
          ) : (
            <div className="divide-y divide-slate-700/50">
              {filteredClients.slice(0, 50).map((client, idx) => (
                <div
                  key={client.row_number || idx}
                  className="px-4 py-3 hover:bg-slate-700/20 transition-colors cursor-pointer grid grid-cols-1 md:grid-cols-12 gap-2 md:gap-4 items-center"
                  onClick={() => handleClientClick(client)}
                >
                  <div className="col-span-1 text-slate-500 text-sm hidden md:block">
                    {client.row_number || idx + 1}
                  </div>
                  <div className="col-span-3">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-full bg-gradient-to-br from-purple-500 to-pink-500 flex items-center justify-center text-white font-bold">
                        {(client.nom || 'C')[0]}{(client.prenom || '')[0]}
                      </div>
                      <div>
                        <p className="font-medium text-white">{client.nom || 'Sans nom'} {client.prenom || ''}</p>
                        <p className="text-xs text-slate-500 md:hidden">{client.id_qhare || 'N/A'}</p>
                      </div>
                    </div>
                  </div>
                  <div className="col-span-2 text-slate-300 text-sm hidden md:block">{client.id_qhare || 'N/A'}</div>
                  <div className="col-span-2 text-slate-300 text-sm hidden md:block">{client.telephones?.[0] || '-'}</div>
                  <div className="col-span-2">
                    <span className={`inline-flex px-2 py-1 rounded-full text-xs font-medium ${
                      client.statut === 'Signé' ? 'bg-emerald-500/20 text-emerald-400' :
                      client.statut === 'RDV Confirmé' ? 'bg-blue-500/20 text-blue-400' :
                      'bg-slate-600/50 text-slate-400'
                    }`}>
                      {client.statut || 'N/A'}
                    </span>
                  </div>
                  <div className="col-span-2 flex justify-end">
                    <button
                      onClick={(e) => { e.stopPropagation(); handleClientClick(client); }}
                      className="flex items-center gap-1 px-3 py-1.5 bg-purple-500 hover:bg-purple-600 text-white rounded-lg text-sm"
                    >
                      <Eye className="w-4 h-4" />
                      Voir Modal
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

      </>)}

      {/* Bannières footer — configurables via banners_footer */}
      {isBannerFooterEnabled("bf_leads_live") && (
        <BanniereLeadsLive
          authUser={authUser}
          onOpenClientFiche={(client) => setSelectedClient(client)}
          onAppelerLead={(lead) => console.log('Appeler lead:', lead)}
        />
      )}
      {isBannerFooterEnabled("bf_nouveau_r1") && (
        <BanniereNouveauR1
          onClientClick={(rowNum) => console.log('Client R1:', rowNum)}
        />
      )}

          </div>{/* end contenu principal col-span-3 */}
        </div>{/* end grid cols-4 */}
      </div>{/* end p-6 wrapper */}

      {/* Modal */}
      {selectedClient && (
        <TestClientModal
          client={selectedClient}
          onClose={() => setSelectedClient(null)}
        />
      )}

    </div>
  );
}
